import * as STD                                               from '@emersya/mpa-std';
import * as CM                                                from '@emersya/mpa-components-manager';
import * as BasketNS                                          from '@emersya/mpa-basket';
import {
	Input,
	ShortcutsManager
}                                                             from '@emersya/mpa-components';
import {
	glMatrix,
	mat4,
	vec3,
	quat
}                                                             from 'gl-matrix';
glMatrix.setMatrixArrayType(Array);
export interface IViewerComponentConfiguration extends CM.Component.IConfiguration {
	readonly emptyProjectCode: string;
	readonly emptyProjectSKU: string;
	readonly productsExtensionById?: Record<string, ViewerComponent.IProductExtension>;
};
export interface IViewerData {
	baseLocalId: number;
	viewerCode: string;
	configurationCode: string | null;
	readonly productInstances: Set<ViewerComponent.ProductInstance>;
};
export interface IViewerSerialized {
	readonly baseLocalId: number;
	readonly viewerCode: string;
	readonly productInstances: ViewerComponent.ProductInstance.ISerialized[];
	readonly configurationCode: string | null;
};
class ViewerComponent extends Input.Component<
	HTMLDivElement,
	IViewerData,
	IViewerSerialized,
	ViewerComponent,
	IViewerComponentConfiguration
> {
	static instance: ViewerComponent | null = null;
	static glMatrix = glMatrix;
	static mat4 = mat4;
	static vec3 = vec3;
	static quat = quat;
	static get self() {
		if (!this.instance) {
			throw STD.errors.user('Viewer is not instanced!', {});
		}
		return this.instance;
	};
	static override initialize() {
		Input.Component.initialize.call(this);
		this.declareEventType('message', '*');
		this.declareEventType('viewer-before-products-extensions-use');
		this.declareEventType('viewer-iframe-loaded');
		this.declareEventType('viewer-project-loaded');
		this.declareEventType('productTypeAdded');
		this.declareEventType('productAdded');
		this.declareEventType('productRemoved');
		this.declareEventType('selection', 'enabled');
		this.declareEventType('selection', 'disabled');
		this.declareEventType('changeViewer', 'start');
		this.declareEventType('changeViewer', 'end');
	};
	debug?: {
		keepRequests: boolean;
	} | false = false;
	viewerWindow: WindowProxy | null = null;
	component_DOM: HTMLDivElement;
	viewerDOM: HTMLObjectElement | HTMLIFrameElement;
	application: TApplicationComponent<any, any>;
	planner: PlannerComponent | null = null;
	globalLocalId = 1;
	categories: ViewerComponent.ProductCategory[] = [];
	currentSelectedInstances: Set<ViewerComponent.ProductInstance> = new Set();
	productsExtensionById: Record<string, ViewerComponent.IProductExtension>;
	menu: ViewerMenuNS.Component | null = null;
	protected isInteractionsEnabled = true;
	private callBaseId = 0;
	private readonly callInfos = <Record<number, {
		stack: string;
		request: ViewerComponent.ICall;
		responseAction: string;
		resolve: (data: ViewerComponent.ICallResponse) => (Promise<void> | void);
		reject: (data: ViewerComponent.ICallResponse) => (Promise<void> | void);
	}>>{};
	loadingDetails: {
		progress: number;
		state: ViewerComponent.TLoadingStates;
	} = {
		progress: 0,
		state: ViewerComponent.TLoadingStates.void
	};
	protected loadingPpack = STD.misc.createPromisePack<void>();
	// private messageQueue: MessageEvent[] = [];
	// private messageQueuePromise?: Promise<void>;
	// Actions that must be awaited and performed in order.
	// Warning: a queued action that calls another queued action will block the queue.
	// TODO rethink this system. Ideally we don't want to need a whitelist
	// private queuedActions = new Set([
	// 	'onProductNodeInstanceDraggingStarted',
	// 	'onProductNodeInstanceDraggingStopped'
	// ]);
	get safe(): Omit<ViewerComponent, 'value'> & { value: NonNullable<ViewerComponent['value']> } {
		if (!this.value) {
			throw STD.errors.user('Viewer is not ready!', {});
		}
		return <Omit<ViewerComponent, 'value'> & { value: NonNullable<ViewerComponent['value']> }>this;
	};
	// Used by components that still depends on a single selected instance.
	get currentSelectedInstance() {
		if (this.currentSelectedInstances.size === 1)
			return this.currentSelectedInstances.values().next().value;
		return undefined;
	}
	private get targetUrl() {
		const endpointURL = ((x) => x.slice(0, x.indexOf('/fake')))(app.clientAppParameters.methodsStore.getViewerURL('fake', {
			forceLocal: !app.clientAppParameters.server.isOnProd && <boolean | undefined>app.parameters.useLocalViewers
		}));
		const v = this.safe.value;
		return `${endpointURL}/${v.viewerCode}${v.configurationCode ? `/${v.configurationCode}` : ''}`;
	}
	override isValueTheDefault() {
		return false;
	};
	override async reset() {
	};
	override async deserialize(serialized: null): Promise<null>;
	override async deserialize(serialized: IViewerSerialized): Promise<IViewerData>;
	override async deserialize(serialized: IViewerSerialized | null) {
		if (!serialized) {
			return null;
		}
		const products = this.categories.map(x => x.products).flat();
		for (const product of products) {
			product.instances.clear();
		}
		const deserialized = <IViewerData>{
			...serialized,
			productInstances: new Set
		};
		if (!serialized.productInstances.length && !serialized.configurationCode) {
			return deserialized;
		}
		if (!app.previousConfigurationMetadata) {
			this.component_throwLibraryError('Cannot deserialize a viewer with data inside if there no application saved configuration metadata!', {
				serialized
			});
		}
		const savedBasketProduct = app.previousConfigurationMetadata.basket.products[0];
		if (!savedBasketProduct) {
			this.component_throwLibraryError('Application saved configuration metadata does not have any product in its basket!', {});
		}
		const tupleProductAndInstance = serialized.productInstances.map(serializedInstance => {
			const Product = products.find(y => y.SKU === serializedInstance.product);
			if (!Product) {
				this.component_throwLibraryError('Serialized product instance is from an unknown product type!', {
					serializedInstance
				});
			}
			return [Product, serializedInstance] as const;
		});
		const isDeserializeSync = true;
		if (isDeserializeSync) {
			for (const tuple of tupleProductAndInstance) {
				deserialized.productInstances.add(await tuple[0].loadInstance(savedBasketProduct, tuple[1]));
			}
		} else {
			await Promise.all(tupleProductAndInstance.map(async tuple => {
				deserialized.productInstances.add(await tuple[0].loadInstance(savedBasketProduct, tuple[1]));
			}));
		}
		return deserialized;
	};
	override async component_onInternalActivation(event: STD.events.Event<any, CM.Component<any, any>>) {
		await super.component_onInternalActivation(event);
		const attribute = this.viewerDOM.tagName.toLowerCase() === 'object' ? 'data' : 'src';
		if (event.subType === 'enabled') {
			const targetUrl = this.targetUrl;
			if (this.viewerDOM.getAttribute(attribute) !== targetUrl) {
				this.viewerDOM.setAttribute(attribute, targetUrl);
			}
			await this.awaitLoading();
		} else {
			this.viewerDOM.removeAttribute(attribute);
			this.loadingDetails = {
				progress: 0,
				state: ViewerComponent.TLoadingStates.void
			};
			this.loadingPpack = STD.misc.createPromisePack<void>();
		}
	};
	protected async createRequest<T extends ViewerComponent.ICallResponse>(
		fromMethod: (this: ViewerComponent, ...x: any[]) => Promise<T>,
		responseAction: string,
		parameters: Record<string, unknown>
	): Promise<T> {
		const VW = this.viewerWindow;
		if (!VW) {
			this.component_throwUserError('Viewer not loaded!', {});
		}
		const id = this.callBaseId++;
		const request: ViewerComponent.ICall = Object.assign({}, parameters, {
			id,
			action: fromMethod.name
		});
		if (
			!['nothing'].includes(request.action)
		) {
			// console.log('createRequest', JSON.stringify(request, null, '  '));
		}
		let result;
		try {
			result = await new Promise((resolve: (data: T) => void, reject) => {
				this.callInfos[id] = {
					stack: (new Error).stack || '',
					resolve: <(data: ViewerComponent.ICallResponse) => void>resolve,
					request,
					responseAction,
					reject
				};
				VW.postMessage(request, '*');
			});
		} catch (error: any) {
			this.component_throwBoundError('Viewer call failed!', {
				input: {
					fromMethod,
					responseAction,
					parameters
				}
			}, error);
			return <any>void 1;// Impossible but ts need it!
		}
		return result;
	};
	private async onIframeLoad() {
		const viewerWindow = (
			this.viewerDOM.contentDocument ? this.viewerDOM.contentDocument.defaultView :
				this.viewerDOM.contentWindow || null
		);
		if (!viewerWindow) {
			console.error('Iframe loaded but doesnt have a document!');
			return;
		}
		const oldWindow = this.viewerWindow;
		this.viewerWindow = viewerWindow;
		this.loadingDetails.state = ViewerComponent.TLoadingStates.iframeLoaded;
		this.onWindowChanged(this.viewerWindow, oldWindow);
		await this.dispatchEvent('viewer-iframe-loaded');
		this.loadingDetails.state = ViewerComponent.TLoadingStates.projectLoading;
		this.viewerWindow.postMessage({
			action: 'registerCallback'
		}, '*');
		if (app.clientAppParameters.app.isTestMode) {
			try {
				(<any>this.viewerWindow).stage.emersya.viewer.verbose = !!app.parameters.viewerVerbose;
			} catch (error: any) {
				void error;
			}
		}
	};
	async onMessage(message: MessageEvent) {
		await this.dispatchMessage(message);
		// if (!this.queuedActions.has(message.data.action)) {
		// 	await this.dispatchMessage(message);
		// 	return;
		// }
		//
		// this.messageQueue.push(message);
		//
		// if (this.messageQueuePromise === undefined)
		// 	this.messageQueuePromise = this.runMessageQueue();
	};
	// async runMessageQueue() {
	// 	while (this.messageQueue.length > 0) {
	// 		const message = this.messageQueue.shift()!;
	// 		await this.dispatchMessage(message);
	// 	}
	//
	// 	this.messageQueuePromise = undefined;
	// }
	async dispatchMessage(message: MessageEvent) {
		const data = <ViewerComponent.ICallResponse>message.data;
		await this.dispatchEvent('message', {
			details: data,
			subType: data.action
		});
		if (typeof data.id === 'number') {
			const actionInfos = this.callInfos[data.id];
			if (!actionInfos) {
				// if (
				// 	(data.action === 'onCurrentMaterialsConfigurationGet') ||
				// 	(data.action === 'onEndCameraTransition') ||
				// 	(/*image transformer*/(data.action === 'onSuccess') &&
				// 		(data.callAction === 'getMeshScreenPosition') &&
				// 		(data.emersyaContext === 'emersyaViewer'))
				// )
				// {
				// 	//this method trigger 2 messages so the second trigger this case because of "delete this.callInfos[data.id];" called on the first one
				return;
				// }
				// throw MPASTD.errors.library(
				// 	`Internal message<${ data.id }> without registered action infos!`,
				// 	{
				// 		data
				// 	}
				// );
			}
			if (data.action !== actionInfos.responseAction) {
				if (data.action === 'onError') {
					await actionInfos.reject(Object.assign({}, data, {
						actionInfos
					}));
					// await actionInfos.reject(Object.assign({}, <ViewerComponent.ICallResponse> data, {
					// 	actionInfos
					// }));
				}
				//is intermediate event, not the final event
				return;
			}
			if (!this.debug || !this.debug.keepRequests) {
				delete this.callInfos[data.id];
			}
			const method = (
				(data.action === 'onError') ||
				(data.action !== actionInfos.responseAction)
			) ? 'reject' : 'resolve';
			if (!data.callAction && (data.action === 'onSuccess')) {
				console.warn('Call response error:', data);
			}
			await actionInfos[method].call(actionInfos, data);
		} else {
			if (data.action == 'onStateChange') {
				const ds = data as any as ViewerComponent.INative_stateChange_event;
				this.loadingDetails.progress = ds.state.loadingProgress;
				if (ds.state.viewerState == 'loaded') {
					this.loadingDetails.state = ViewerComponent.TLoadingStates.projectLoaded;
					await this.enableKeyboardTrackingMode();
					await this.dispatchEvent('viewer-project-loaded');
					this.loadingPpack.resolve();
				}
			}
		}
	};
	private async onKeyPress(event: STD.events.Event<any, CM.Component<any, any>>) {
		const shortcutsManager = <ShortcutsManager.Component>app.component_find('@.shortcutsManager')!;
		await shortcutsManager.onKeyDown(event.details.key, event.details.code);
	};
	private onKeyRelease(event: STD.events.Event<any, CM.Component<any, any>>) {
		const shortcutsManager = <ShortcutsManager.Component>app.component_find('@.shortcutsManager')!;
		shortcutsManager.onKeyUp(event.details.key, event.details.code);
	};
	private onWindowChanged(newWindow: Window | null, oldWindow: Window | null) {
		if (oldWindow) {
			this.application.messagesListeners.delete(oldWindow);
		}
		if (newWindow) {
			this.application.messagesListeners.set(newWindow, this);
		}
	};
	override async component_appendOuterChild(
		component: CM.Component<any, any>,
		_slot: string | HTMLElement
	) {
		if (component.static.type === 'viewer-planner') {
			if (this.planner) {
				this.component_throwUserError('There already a planner!', {});
			}
			this.planner = <PlannerComponent>component;
		}
		await super.component_appendOuterChild(component, _slot);
	};
	override async component_initialize() {
		await this.dispatchEvent('viewer-before-products-extensions-use');
		for (const extension of Object.values(this.productsExtensionById)) {
			if (extension.configuratorPrepare && !(extension.configuratorPrepare instanceof CM.PropertyOperation)) {
				(<any>extension).configuratorPrepare = CM.PropertyOperation.create(extension.configuratorPrepare);
			}
			if (extension.configuratorGet && !(extension.configuratorGet instanceof CM.PropertyOperation)) {
				(<any>extension).configuratorGet = CM.PropertyOperation.create(extension.configuratorGet);
			}
			if (extension.configuratorClean && !(extension.configuratorClean instanceof CM.PropertyOperation)) {
				(<any>extension).configuratorClean = CM.PropertyOperation.create(extension.configuratorClean);
			}
		}
		await super.component_initialize();
		// await this.planner.initialize();
	};
	override async component_start(event: STD.events.Event<any, CM.Component<any, any>>) {
		// const extendedProducts = (
		// 	this.categories.map(x => x.products.filter(y => y.extension)).filter(x => x.length).flat()
		// );
		// for (const extendedProduct of extendedProducts) {
		// 	const extension = <NonNullable<typeof extendedProduct.extension>> extendedProduct.extension;
		// 	const id = 'plannerProduct.' + extendedProduct.getId();
		// 	if (extension.name) {
		// 		STD.translationsManager.addObject(id + '.name', extension.name);
		// 	}
		// 	if (extension.description) {
		// 		STD.translationsManager.addObject(id + '.description', extension.description);
		// 	}
		// }
		await super.component_start(event);
	};
	// override async updateValue(
	// 	newValue : IViewerData,
	// 	_defaultValueMarker? : Symbol
	// )
	// {
	// 	void newValue;
	// 	void _defaultValueMarker;
	// 	const oldValue = this.value;
	// 	void oldValue;
	// 	await this.dispatchEvent('changeViewer', {
	// 		subType : 'start'
	// 	});
	// 	await super.updateValue(newValue, _defaultValueMarker);
	// 	await this.updateTarget();
	// 	await this.awaitLoading();
	// 	await this.dispatchEvent('changeViewer', {
	// 		subType : 'end'
	// 	});
	// 	// if (
	// 	// 	(_defaultValueMarker && Input.Component.isDefaultValueMarker(_defaultValueMarker)) ||
	// 	// 	!this.component_isStarted ||
	// 	// 	(<any> window).app.isReloading
	// 	// )
	// 	// {
	// 	// 	return;
	// 	// }
	// 	// if (this.value !== newValue) {
	// 	// 	this.component_throwLibraryError('');
	// 	// }
	// 	// if (!oldValue || (oldValue.enabled !== this.value.enabled)) {
	// 	// 	const graphicsActivation = <InputBool.Component> this.component_find('@:header.enable');
	// 	// 	await graphicsActivation.updateValue(this.value.enabled);
	// 	// }
	// 	// if (!oldValue || (oldValue.leftRight !== this.value.leftRight)) {
	// 	// 	const leftRightRange = <InputNumber.Component> this.component_find('@:editor.move-leftRight.input');
	// 	// 	await leftRightRange.updateValue(this.value.leftRight);
	// 	// }
	// 	// if (!oldValue || (oldValue.upDown !== this.value.upDown)) {
	// 	// 	const upDownRange = <InputNumber.Component> this.component_find('@:editor.move-upDown.input');
	// 	// 	await upDownRange.updateValue(this.value.upDown);
	// 	// }
	// 	// if (!oldValue || (oldValue.resize !== this.value.resize)) {
	// 	// 	const resizeRange = <InputNumber.Component> this.component_find('@:editor.resize.input');
	// 	// 	await resizeRange.updateValue(this.value.resize);
	// 	// }
	// 	// if (!oldValue || (oldValue.rotate !== this.value.rotate)) {
	// 	// 	const rotateRange = <InputNumber.Component> this.component_find('@:editor.rotate.input');
	// 	// 	await rotateRange.updateValue(this.value.rotate);
	// 	// }
	// 	// if (!oldValue || (oldValue.image !== this.value.image)) {
	// 	// 	const inputImage = <InputImage.Component> this.component_find('@:editor.image');
	// 	// 	await inputImage.updateValue(this.value.image);
	// 	// }
	// 	// await super.updateValue(newValue, _defaultValueMarker);
	// };
		async getViewerState(): Promise<ViewerComponent.INative_stateChange_event> {
		return await this.createRequest(this.getViewerState, 'getViewerState', {});
	};
	async addProductNodeInstance(desc: ViewerComponent.INative_addProductNodeInstance_descriptor): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.addProductNodeInstance, 'onSuccess', <any>desc);
	};
	async addProductNodesInstances(desc: ViewerComponent.INative_addProductNodeInstance_descriptor[]): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.addProductNodesInstances, 'onSuccess', {
			nodes: desc
		});
	};
	async setProductNodesInstancesMatrices(desc: ViewerComponent.INative_setProductNodesInstancesMatrices_descriptor): Promise<ViewerComponent.ICallResponse> {
		const result = await this.createRequest(this.setProductNodesInstancesMatrices, 'onSuccess', {
			nodes: desc
		});
		if (!this.value) {
			return result;
		}
		const instances = Array.from(this.value.productInstances);
		for (const node of desc) {
			const instance = instances.find(x => x.localId === node.localId);
			if (instance) {
				const position = ViewerComponent.positionFromMatrix(node.matrix);
				instance.position.scene = {
					...position.position,
					rotation: {...position.rotation}
				};
			}
		}
		return result;
	};
	async freezeRendering(desc: ViewerComponent.INative_freezeRendering_descriptor = {}): Promise<ViewerComponent.ICallResponse> {
		// return Promise.resolve({
		// 	id :         0,
		// 	action :     'onSuccess',
		// 	callAction : 'freezeRendering'
		// });
		return await this.createRequest(this.freezeRendering, 'onSuccess', <any>desc);
	};
	async unfreezeRendering(desc: ViewerComponent.INative_unfreezeRendering_descriptor = {}): Promise<ViewerComponent.ICallResponse> {
		// return Promise.resolve({
		// 	id :         0,
		// 	action :     'onSuccess',
		// 	callAction : 'freezeRendering'
		// });
		return await this.createRequest(this.unfreezeRendering, 'onSuccess', <any>desc);
	};
	async setProductNodesInstancesDraggable(desc: ViewerComponent.INative_setProductNodesInstancesDraggable_descriptor): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.setProductNodesInstancesDraggable, 'onSuccess', <any>desc);
	};
	async enableMouseTrackingMode(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.enableMouseTrackingMode, 'onSuccess', {});
	};
	async setCamera(desc: ViewerComponent.INative_setCamera_descriptor): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.setCamera, 'onSuccess', {
			position: desc.position,
			target: desc.target,
			up: desc.up,
			transitionTime: desc.transitionTime,
			fov: desc.fov,
			cameraControl: desc.cameraControl,
			checkCurrentPosition: desc.checkCurrentPosition
		});
	};
	async getScreenshot(desc: ViewerComponent.INative_getScreenshot_descriptor): Promise<ViewerComponent.INative_getScreenshot_response> {
		return await this.createRequest(this.getScreenshot, 'onScreenshot', {
			takeBackground: desc.takeBackground,
			transparentBackground: desc.transparentBackground,
			width: desc.width,
			height: desc.height,
			camera: desc.camera
		});
	};
	async getScreenshots(desc: ViewerComponent.INative_getScreenshots_descriptor): Promise<ViewerComponent.INative_getScreenshots_response> {
		return await this.createRequest(this.getScreenshots, 'onScreenshots', {
			takeBackground: desc.takeBackground,
			viewpoints: 'viewpoints' in desc ? desc.viewpoints : undefined,
			width: desc.width,
			height: desc.height,
			cameras: 'cameras' in desc ? desc.cameras : undefined
		});
	};
	async getCamera(): Promise<ViewerComponent.INative_getCamera_response> {
		return await this.createRequest(this.getCamera, 'onGetCamera', {});
	};
	async rotateCameraX(angle: number): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.rotateCameraX, 'onSuccess', {value: angle});
	};
	async rotateCameraY(angle: number): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.rotateCameraY, 'onSuccess', {value: angle});
	};
	async rotateCameraZ(angle: number): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.rotateCameraZ, 'onSuccess', {value: angle});
	};
	async rotateCamera(angles: ViewerComponent.TVector3): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.rotateCamera, 'onSuccess', {value: angles});
	};
	async startViewer(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.startViewer, 'onSuccess', {});
	};
	async stopViewer(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.stopViewer, 'onSuccess', {});
	};
	async preloadViewerData(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.preloadViewerData, 'onSuccess', {});
	};
	async play(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.play, 'onSuccess', {});
	};
	async pause(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.pause, 'onSuccess', {});
	};
	async resetCamera(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.resetCamera, 'onSuccess', {});
	};
	async startZoomIn(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.startZoomIn, 'onSuccess', {});
	};
	async stopZoomIn(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.stopZoomIn, 'onSuccess', {});
	};
	async startZoomOut(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.startZoomOut, 'onSuccess', {});
	};
	async stopZoomOut(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.stopZoomOut, 'onSuccess', {});
	};
	async toggleKeyboard(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.toggleKeyboard, 'onSuccess', {});
	};
	async enableKeyboard(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.enableKeyboard, 'onSuccess', {});
	};
	async disableKeyboard(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.disableKeyboard, 'onSuccess', {});
	};
	async toggleMouseButtons(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.toggleMouseButtons, 'onSuccess', {});
	};
	async enableMouseButtons(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.enableMouseButtons, 'onSuccess', {});
	};
	async disableMouseButtons(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.disableMouseButtons, 'onSuccess', {});
	};
	async toggleMouseMove(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.toggleMouseMove, 'onSuccess', {});
	};
	async enableMouseMove(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.enableMouseMove, 'onSuccess', {});
	};
	async disableMouseMove(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.disableMouseMove, 'onSuccess', {});
	};
	async toggleMouseWheel(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.toggleMouseWheel, 'onSuccess', {});
	};
	async enableMouseWheel(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.enableMouseWheel, 'onSuccess', {});
	};
	async disableMouseWheel(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.disableMouseWheel, 'onSuccess', {});
	};
	async toggleTouchInteractions(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.toggleTouchInteractions, 'onSuccess', {});
	};
	async enableTouchInteractions(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.enableTouchInteractions, 'onSuccess', {});
	};
	async disableTouchInteractions(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.disableTouchInteractions, 'onSuccess', {});
	};
	async toggleHighFrequencyFeedback(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.toggleHighFrequencyFeedback, 'onSuccess', {});
	};
	async enableHighFrequencyFeedback(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.enableHighFrequencyFeedback, 'onSuccess', {});
	};
	async disableHighFrequencyFeedback(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.disableHighFrequencyFeedback, 'onSuccess', {});
	};
	async toggleMouseTracking(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.toggleMouseTracking, 'onSuccess', {});
	};
	async enableMouseTracking(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.enableMouseTracking, 'onSuccess', {});
	};
	async disableMouseTracking(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.disableMouseTracking, 'onSuccess', {});
	};
	async toggleCameraLock(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.toggleCameraLock, 'onSuccess', {});
	};
	async unlockCamera(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.unlockCamera, 'onSuccess', {});
	};
	async lockCamera(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.lockCamera, 'onSuccess', {});
	};
	async switchExperienceType(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.switchExperienceType, 'onSuccess', {});
	};
	async changeLanguage(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.changeLanguage, 'onSuccess', {});
	};
	async openAnnotationPin(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.openAnnotationPin, 'onSuccess', {});
	};
	async resetMaterials(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.resetMaterials, 'onSuccess', {});
	};
	async setHighlight(
		desc: {
			meshes?: string[];
			configurableMaterials?: string[];
			configurableMaterialGroups?: string[];
			nodeLocalIds?: number[];
			cursor?: string;
		}
	): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.setHighlight, 'onSuccess', desc);
	};
	async getForcedResolutionScreenshot(desc: ViewerComponent.INative_getScreenshot_descriptor): Promise<ViewerComponent.INative_getScreenshot_response> {
		return await this.createRequest(this.getForcedResolutionScreenshot, 'onScreenshot', {
			takeBackground: desc.takeBackground,
			width: desc.width,
			height: desc.height,
			viewpoints: 'viewpoints' in desc ? desc.viewpoints : undefined,
			cameras: 'cameras' in desc ? desc.cameras : undefined
		});
	};
	async getForcedResolutionScreenshots(desc: ViewerComponent.INative_getScreenshots_descriptor): Promise<ViewerComponent.INative_getScreenshots_response> {
		return await this.createRequest(this.getForcedResolutionScreenshots, 'onScreenshots', {
			takeBackground: desc.takeBackground,
			width: desc.width,
			height: desc.height,
			viewpoints: 'viewpoints' in desc ? desc.viewpoints : undefined,
			cameras: 'cameras' in desc ? desc.cameras : undefined
		});
	};
	async setZoomProportion(desc: ViewerComponent.INative_setZoomProportion_descriptor): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.setZoomProportion, 'onSuccess', {
			value: desc.value,
			transitionTime: desc.transitionTime
		});
	};
	async moveCameraX(value: number): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.moveCameraX, 'onSuccess', {
			value: value
		});
	};
	async moveCameraY(value: number): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.moveCameraY, 'onSuccess', {
			value: value
		});
	};
	async moveCameraZ(value: number): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.moveCameraZ, 'onSuccess', {
			value: value
		});
	};
	async moveCamera(value: ViewerComponent.TVector3): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.moveCamera, 'onSuccess', {
			value: value
		});
	};
	async translateCameraX(value: number): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.translateCameraX, 'onSuccess', {
			value: value
		});
	};
	async translateCameraY(value: number): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.translateCameraZ, 'onSuccess', {
			value: value
		});
	};
	async translateCameraZ(value: number): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.translateCameraZ, 'onSuccess', {
			value: value
		});
	};
	async translateCamera(value: ViewerComponent.TVector3): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.translateCamera, 'onSuccess', {
			value: value
		});
	};
	async setPlayMode(mode: 'NORMAL' | 'ANNOTATIONS'): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.setPlayMode, 'onSuccess', {
			value: mode
		});
	};
	async setSceneryBackgroundColor(color: string): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.setSceneryBackgroundColor, 'onSuccess', {
			color
		});
	};
	async setARControlMode(): Promise<ViewerComponent.ICallResponse> {
		//doc is not updated so maybe not correct signature
		return await this.createRequest(this.setARControlMode, 'onSuccess', {});
	};
	async setCursor(cursor: string): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.setCursor, 'onSuccess', {
			value: cursor
		});
	};
	async toggleRenderingFreezing(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.toggleRenderingFreezing, 'onSuccess', {});
	};
	async closeAnnotationPin(number: number): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.closeAnnotationPin, 'onSuccess', {
			number
		});
	};
	async toggleAnnotationPin(number: number): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.toggleAnnotationPin, 'onSuccess', {
			number
		});
	};
	async showAnnotationPin(number: number): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.showAnnotationPin, 'onSuccess', {
			number
		});
	};
	async showAnnotationsPin(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.showAnnotationsPin, 'onSuccess', {});
	};
	async hideAnnotationsPin(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.hideAnnotationsPin, 'onSuccess', {});
	};
	async toggleAnnotationsPin(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.toggleAnnotationsPin, 'onSuccess', {});
	};
	async updateAnnotationPin(desc: ViewerComponent.INative_updateAnnotationPin_descriptor): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.updateAnnotationPin, 'onSuccess', {
			annotationId: desc.annotationId,
			title: desc.title,
			description: desc.description
		});
	};
	async addAnnotationPin(desc: ViewerComponent.INative_addAnnotationPin_descriptor): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.addAnnotationPin, 'onSuccess', {
			annotationId: desc.annotationId,
			number: desc.number,
			title: desc.title,
			description: desc.description,
			onNextClick: desc.onNextClick,
			nodeId: desc.nodeId,
			localId: desc.localId,
			position: desc.position
		});
	};
	async removeAnnotationPin(desc: ViewerComponent.INative_removeAnnotationPin_descriptor): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.removeAnnotationPin, 'onSuccess', {
			annotationId: desc.annotationId
		});
	};
	async startAr(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.startAr, 'onSuccess', {});
	};
	async stopAr(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.stopAr, 'onSuccess', {});
	};
	async showArHelper(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.showArHelper, 'onSuccess', {});
	};
	async hideArHelper(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.hideArHelper, 'onSuccess', {});
	};
	async showArMenu(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.showArMenu, 'onSuccess', {});
	};
	async hideArMenu(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.hideArMenu, 'onSuccess', {});
	};
	async toggleARShareButton(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.toggleARShareButton, 'onSuccess', {});
	};
	async collapseARShareButton(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.collapseARShareButton, 'onSuccess', {});
	};
	async expandARShareButton(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.expandARShareButton, 'onSuccess', {});
	};
	async showHelp(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.showHelp, 'onSuccess', {});
	};
	async hideHelp(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.hideHelp, 'onSuccess', {});
	};
	async toggleHelp(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.toggleHelp, 'onSuccess', {});
	};
	async setPredefinedViewpoint(viewpoint: number): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.setPredefinedViewpoint, 'onSuccess', {
			number: viewpoint
		});
	};
	async hideAnimationTriggers(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.hideAnimationTriggers, 'onSuccess', {});
	};
	async showAnimationTriggers(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.showAnimationTriggers, 'onSuccess', {});
	};
	async toggleAnimationTriggers(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.toggleAnimationTriggers, 'onSuccess', {});
	};
	async setViewpoint(
		desc: {
			readonly name: string;
			readonly transitionTime?: number;
		}
	): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.setViewpoint, 'onSuccess', {
			name: desc.name,
			transitionTime: desc.transitionTime
		});
	};
	async setAnimationTriggerType(
		desc: {
			type: string;
			triggerId: number;
		}
	): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.setAnimationTriggerType, 'onSuccess', <any>desc);
	};
// async LVL2() {
// 	return (this.createRequest(LVL2, 'onError', {}));
// };
// (function(globals){
// 	function EmersyaViewerSTD_2(EmersyaViewer){
// 		this.viewer = EmersyaViewer;
// 		new globals.EmersyaViewerSTD_1(this.viewer);
// 		this.viewer.constructor.prototype.togglePresetsMenu = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'togglePresetsMenu'
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.openPresetsMenu = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'openPresetsMenu'
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.closePresetsMenu = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'closePresetsMenu'
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.resetAnimations = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'resetAnimations'
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.getCurrentAnimationStates = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'getCurrentAnimationStates'
// 			};
// 			return this.createPromise("onCurrentAnimationStates", request);
// 		};
// 		this.viewer.constructor.prototype.triggerAnimation = function(params){
// 			var request = {
// 				id        : ++this.callId,
// 				action    : 'triggerAnimation',
// 				triggerId : params.triggerId
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.setAnimationTriggerColor = function(params){
// 			var request = {
// 				id        : ++this.callId,
// 				action    : 'setAnimationTriggerColor',
// 				triggerId : params.triggerId,
// 				color     : params.color
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.setAnimationTriggersColor = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'setAnimationTriggersColor',
// 				color  : params.color
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.showAnimationTriggers = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'showAnimationTriggers'
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.hideAnimationTriggers = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'hideAnimationTriggers'
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.toggleAnimationTriggers = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'toggleAnimationTriggers'
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.setAnimationSpeed = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'setAnimationSpeed',
// 				value  : params.value
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 	};
// 	globals.EmersyaViewerSTD_2  = EmersyaViewerSTD_2;
// }(this));
	async setMaterials(desc: ViewerComponent.INative_setMaterials_descriptor): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.setMaterials, 'onSuccess', {
			materials: desc.materials,
			nodeLocalIds: desc.nodeLocalIds
		});
	};
	async saveConfiguration(
		parameters: {
			screenshot?: boolean;
			shortLink?: boolean;
			metadata?: Record<string, unknown>;
			unique?: boolean;
		} = {}
	): Promise<ViewerComponent.INative_saveConfiguration_response> {
		return await this.createRequest(this.saveConfiguration, 'onSavedConfiguration', {
			screenshot: parameters.screenshot || false,
			shortLink: parameters.shortLink || false,
			metadata: parameters.metadata || undefined,
			unique: parameters.unique || false
		});
	};
	async saveModularConfiguration(
		parameters: {
			screenshot?: boolean;
			shortLink?: boolean;
			metadata?: Record<string, unknown>;
			unique?: boolean;
		} = {}
	): Promise<ViewerComponent.INative_saveConfiguration_response> {
		return await this.createRequest(this.saveModularConfiguration, 'onSavedModularConfiguration', {
			screenshot: parameters.screenshot || false,
			shortLink: parameters.shortLink || false,
			metadata: parameters.metadata ? JSON.stringify(parameters.metadata) : undefined,
			unique: parameters.unique || false
		});
	};
	async loadConfiguration(
		parameters: {
			configurationCode: string;
		}
	): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.loadConfiguration, 'onSuccess', {
			code: parameters.configurationCode
		});
	};
	async setMaterialsGroups(
		parameters: ViewerComponent.INative_setMaterialsGroups_descriptor
	): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.setMaterialsGroups, 'onSuccess', {
			values: parameters.values,
			nodeLocalIds: parameters.nodeLocalIds
		});
	};
	async transformTexture(
		desc: {
			configurableMaterial: string;
			materialVariation: string;
			contents: ViewerComponent.TPBRContent[];
			transform: {
				translation: [number, number, number];
				scaling: [number, number, number];
				rotationCenter: [number, number, number];
				rotation: number;
			};
		}
	): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.transformTexture, 'onSuccess', {
			data: {
				configurableMaterial: desc.configurableMaterial,
				materialVariation: desc.materialVariation,
				contents: desc.contents,
				transform: {
					translation: desc.transform.translation,
					scaling: desc.transform.scaling,
					rotationCenter: desc.transform.rotationCenter,
					rotation: desc.transform.rotation
				}
			}
		});
	};
	async getCurrentMaterials(): Promise<ViewerComponent.INative_getCurrentMaterials_response> {
		return await this.createRequest(this.getCurrentMaterials, 'onCurrentMaterialSettingsConfiguration', {});
	};
	async takeSaveScreenshotGetURL(
		desc: ViewerComponent.INative_getScreenshot_descriptor
	): Promise<ViewerComponent.INative_getScreenshot_response> {
		return await this.createRequest(this.takeSaveScreenshotGetURL, 'onSavedScreenshot', {
			takeBackground: desc.takeBackground,
			transparentBackground: desc.transparentBackground,
			width: desc.width,
			height: desc.height,
			camera: desc.camera
		});
	};
	async updateCustomTexture(desc: ViewerComponent.INative_updateCustomTexture_descriptor): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.updateCustomTexture, 'onSuccess', {
			data: {
				configurableMaterial: desc.configurableMaterial,
				materialVariation: desc.materialVariation,
				image: desc.image,
				contents: desc.contents
			}
		});
	};
	async updateCustomColor(
		desc: {
			readonly  groupName?: string;
			readonly  configurationName?: string;
			readonly  configurableMaterial?: string;
			readonly  materialVariation?: string;
			readonly  color: string;
			readonly  contents: string[];
		}
	): Promise<ViewerComponent.ICallResponse> {
		if (
			(!desc.groupName || !desc.configurationName) &&
			(!desc.configurableMaterial || !desc.materialVariation)
		) {
			throw new Error;
		}
		return await this.createRequest(this.updateCustomColor, 'onSuccess', {
			groupName: desc.groupName,
			configurationName: desc.configurationName,
			configurableMaterial: desc.configurableMaterial,
			materialVariation: desc.materialVariation,
			color: desc.color,
			contents: desc.contents
		});
	};
	async setMaterialsGroup(
		parameters: ViewerComponent.TNative_setMaterialsGroup_descriptor
	): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.setMaterialsGroup, 'onSuccess', <Record<string, unknown>><any>parameters);
	};
	async setPreset(
		parameters: ViewerComponent.TNative_setPreset_descriptor
	): Promise<ViewerComponent.ICallResponse> {
		if ('name' in parameters) {
			return await this.createRequest(this.setPreset, 'onSuccess', {
				presetName: parameters.name,
				nodeLocalIds: parameters.nodeLocalIds
			});
		} else {
			return await this.createRequest(this.setPreset, 'onSuccess', {
				presetSku: parameters.SKU,
				nodeLocalIds: parameters.nodeLocalIds
			});
		}
	}
	async updateCustomTextsRealDimensions(
		desc: {
			readonly configurableMaterial: string;
			readonly materialVariation: string;
			readonly contents: string[];
			readonly color: string;
			readonly backgroundColor: string;
			readonly italic: boolean;
			readonly bold: boolean;
			readonly imageWidth: number;
			readonly imageHeight: number;
			readonly verticalAlignment: string;
			readonly horizontalAlignment: string;
			readonly size: number;
			readonly textOffsetY: number;
			readonly textOffsetX: number;
			readonly texts: {}[];
			readonly font: string;
			readonly underline: boolean;
			readonly strokeText: boolean;
			readonly strokeColor: string;
			readonly strokeWidth: number;
			readonly uvRatio: number;
			readonly surfaceWidth: number;
			readonly maxTextWidth: number;
			readonly maxTextHeight: number;
			readonly textAlignment: string;
			readonly mode: string;
		}
	): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.updateCustomTextsRealDimensions, 'onSuccess', {
			data: {
				configurableMaterial: desc.configurableMaterial,
				materialVariation: desc.materialVariation,
				contents: desc.contents,
				color: desc.color,
				backgroundColor: desc.backgroundColor,
				italic: desc.italic,
				bold: desc.bold,
				imageWidth: desc.imageWidth,
				imageHeight: desc.imageHeight,
				verticalAlignment: desc.verticalAlignment,
				horizontalAlignment: desc.horizontalAlignment,
				size: desc.size,
				textOffsetY: desc.textOffsetY,
				textOffsetX: desc.textOffsetX,
				texts: desc.texts,
				font: desc.font,
				underline: desc.underline,
				strokeText: desc.strokeText,
				strokeColor: desc.strokeColor,
				strokeWidth: desc.strokeWidth,
				uvRatio: desc.uvRatio,
				surfaceWidth: desc.surfaceWidth,
				maxTextWidth: desc.maxTextWidth,
				maxTextHeight: desc.maxTextHeight,
				textAlignment: desc.textAlignment,
				mode: desc.mode
			}
		});
	};
	async updateTextureSettings(
		desc: any
	): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.updateTextureSettings, 'onSuccess', {
			data: {
				...desc
			}
		});
	};
	async getCurrentMaterialsGroup(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.getCurrentMaterialsGroup, 'onCurrentMaterialsGroupGet', {
			data: {}
		});
	};
// async getCurrentMaterialTreesConfigurationByNode() : Promise<ViewerComponent.ICallResponse> {
// 	return await this.createRequest(getCurrentMaterialTreesConfigurationByNode, 'onCurrentMaterialTreesConfigurationByNodeGet', {
// 		data : {}
// 	});
// };
	async getCurrentGlobalConfiguration(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.getCurrentGlobalConfiguration, 'onGetCurrentGlobalConfiguration', {
			data: {}
		});
	};
	async getCurrentCustomTexturesSettings(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.getCurrentCustomTexturesSettings, 'onGetCurrentCustomTexturesSettings', {
			data: {}
		});
	};
// 		this.viewer.constructor.prototype.getCurrentCustomTextsSettings = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'getCurrentCustomTextsSettings',
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.updateCustomTextureWithPattern = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'updateCustomTextureWithPattern',
// 				data   : {
// 					configurableMaterial : params.configurableMaterial,
// 					materialVariation    : params.materialVariation,
// 					pattern              : params.pattern,
// 					fuzz                 : params.fuzz,
// 					colors               : params.colors,
// 					contents             : params.contents
// 				}
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.updateCustomText = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'updateCustomText',
// 				data   : {
// 					configurableMaterial : params.configurableMaterial,
// 					materialVariation    : params.materialVariation,
// 					contents             : params.contents,
// 					color                : params.color,
// 					backgroundColor      : params.backgroundColor,
// 					italic               : params.italic,
// 					bold                 : params.bold,
// 					imageWidth           : params.imageWidth,
// 					imageHeight          : params.imageHeight,
// 					verticalAlignment    : params.verticalAlignment,
// 					horizontalAlignment  : params.horizontalAlignment,
// 					size                 : params.size,
// 					textOffsetY          : params.textOffsetY,
// 					textOffsetX          : params.textOffsetX,
// 					text                 : params.text,
// 					font                 : params.font,
// 					underline            : params.underline,
// 					strokeText           : params.strokeText,
// 					strokeColor          : params.strokeColor,
// 					strokeWidth          : params.strokeWidth,
// 				}
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.updateCustomTexts = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'updateCustomTexts',
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.getFallbackScreenshots = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'getFallbackScreenshots',
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.takeSaveServerScreenshotGetURL = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'takeSaveServerScreenshotGetURL',
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.takeSaveServerScreenshotsGetURL = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'takeSaveServerScreenshotsGetURL',
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.takeSaveCroppedScreenshotFromFallbackGetURL = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'takeSaveCroppedScreenshotFromFallbackGetURL',
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.takeSaveServerScreenshotGetURLCustomConfiguration = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'takeSaveServerScreenshotGetURLCustomConfiguration',
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.saveBasket = function(basket){
// 			var billingAddress  = (Object.keys(basket.billingAddress ).length !== 0) ? basket.billingAddress  : null;
// 			var deliveryAddress = (Object.keys(basket.deliveryAddress).length !== 0) ? basket.deliveryAddress : null;
// 			var appCode = '';
// 			try {
// 				appCode = location.pathname.match(/(?:(?:testApp)|(?:app))\/(\w+)/)[1];
// 			} catch(e) {}
// 			var request = {
// 				id              : ++this.callId,
// 				action          : 'saveBasket',
// 				currency        : basket.currency,
// 				discountContext : basket.discountContext,
// 				discountType    : basket.discount.type,
// 				discount        : basket.discount.value,
// 				taxesContext    : basket.taxesContext,
// 				price           : basket.totalPrice.inclTax,
// 				billingAddress  : billingAddress,
// 				deliveryAddress : deliveryAddress,
// 				metaData        : basket.metaData,
// 				locale          : basket.locale,
// 				country         : basket.country,
// 				state           : basket.state,
// 				products        : basket.products,
// 				isQuoteRequest  : basket.isQuoteRequest,
// 				testMode        : this.settings.testMode,
// 				appCode         : appCode
// 			};
// 			if (basket.reference) {
// 				request.reference = basket.reference.value;
// 			}
// 			return this.createPromise("onSavedBasket", request);
// 		};
// 		this.viewer.constructor.prototype.saveProduct = function(params){
// 			var request = {
// 				id                            : ++this.callId,
// 				action                        : 'saveProduct',
// 				basePrice                     : params.basePrice,
// 				basePriceTax                  : params.basePriceTax,
// 				unitPrice                     : params.unitPrice,
// 				quantity                      : params.quantity,
// 				discountType                  : params.discountType,
// 				discount                      : params.discount,
// 				amountWithoutTax              : params.amountWithoutTax,
// 				amountWithTax                 : params.amountWithTax,
// 				name                          : params.name,
// 				code                          : params.code,
// 				thumbURL                      : params.thumbnailURL,
// 				basketCode                    : params.basketCode,             // used to attach a product to a basket
// 				savedConfigurationCode        : params.savedConfigurationCode, // used to attach a product to a saved configuration
// 				metadata                      : params.metadata,               // used to save a configuration details on S3
// 				testMode                      : this.settings.testMode,
// 				appCode                       : params.appCode || null,
// 				appConfigurationFileVersionId : params.appConfigurationFileVersionId || null
// 			};
// 			return this.createPromise("onSavedProduct", request);
// 		};
// 		this.viewer.constructor.prototype.saveProductConfiguration = function(params){
// 			var request = {
// 				id         : ++this.callId,
// 				action     : "saveProductConfiguration",
// 				shortLink  : params.shortLink,
// 				metadata   : params.metadata,               // used to save a configuration details on S3
// 			};
// 			return this.createPromise("saveProductConfiguration", request);
// 		};
// //
// 		this.viewer.constructor.prototype.setTextureLayer = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'setTextureLayer',
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.removeTextureLayer = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'removeTextureLayer',
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.exportTextureImage = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'exportTextureImage',
// 				data   : {
// 					configurableMaterial : params.configurableMaterial,
// 					materialVariation    : params.materialVariation,
// 					crop                 : params.crop,
// 					contents             : params.contents
// 				}
// 			};
// 			return this.createPromise("onGetExportedTexture", request);
// 		};
// 		this.viewer.constructor.prototype.exportTextureImageGetURL = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'exportTextureImageGetURL',
// 				data : {
// 					configurableMaterial : params.configurableMaterial,
// 					materialVariation    : params.materialVariation,
// 					contents             : params.contents,
// 					crop                 : params.crop,
// 				}
// 			};
// 			return this.createPromise("onGetExportedTextureURL", request);
// 		};
// 		this.viewer.constructor.prototype.updateProduct = function(params){
// 			var request = {
// 				id               : ++this.callId,
// 				action           : 'updateProduct',
// 				updateAttributes : params.updateAttributes,
// 				selectOptions    : params.selectOptions,
// 				unselectOptions  : params.unselectOptions,
// 				toggleOptions    : params.toggleOptions
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 		this.viewer.constructor.prototype.getProduct = function(params){
// 			var request = {
// 				id     : ++this.callId,
// 				action : 'getProduct',
// 			};
// 			return this.createPromise("onSuccess", request);
// 		};
// 	};
// 	globals.EmersyaViewerSTD_3  = EmersyaViewerSTD_3;
// }(this));
// export interface ITransformation {
// };
	async getPlannerUsableProjects(): Promise<ViewerComponent.INative_getPlannerUsableProjects_response> {
		return await this.createRequest(this.getPlannerUsableProjects, 'onSuccess', {});
	};
	async startProductNodeDrop(desc: ViewerComponent.INative_startProductNodeDrop_node_descriptor[]): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.startProductNodeDrop, 'onSuccess', {
			nodesToAdd: desc
		});
	};
	async stopProductNodeDrag(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.stopProductNodeDrag, 'onSuccess', {});
	};
	async removeProductNodeInstance(desc: number): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.removeProductNodeInstance, 'onSuccess', {
			localId: desc
		});
	};
	async removeProductNodesInstances(desc: number[]): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.removeProductNodesInstances, 'onSuccess', {
			localIds: desc
		});
	};
	async resizePlannerSpace(
		desc: {
			length: number;
			width: number;
			height: number;
		}
	): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.resizePlannerSpace, 'onSuccess', {
			length: desc.length,
			width: desc.width,
			height: desc.height
		});
	};
	async transformMeshInstancesTextures(desc: ViewerComponent.INative_transformMeshInstancesTextures_descriptor[]): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.transformMeshInstancesTextures, 'onSuccess', {
			meshes: desc
		});
	};
	async updateMeshInstanceCustomTextures(desc: ViewerComponent.INative_updateMeshInstanceCustomTextures_descriptor[]): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.updateMeshInstanceCustomTextures, 'onSuccess', {
			meshes: desc
		});
	};
	async selectProductNode(
		desc: {
			localId: number;
		}
	): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.selectProductNode, 'onSuccess', {
			localId: desc.localId,
			value: true
		});
	};
	async unselectProductNode(
		desc: {
			localId: number;
		}
	): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.selectProductNode, 'onSuccess', {
			localId: desc.localId,
			value: false
		});
	};
	async setProductNodesInstancesTransformations(
		desc: {
			nodes: {
				localId: number;
				position?: ViewerComponent.TVector3;
				rotationAxis?: ViewerComponent.TVector3;
				rotationAngle?: number;
			}[];
		}
	): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.setProductNodesInstancesTransformations, 'onSuccess', {
			nodes: desc.nodes
		});
	};
	async directCall(args: any[]): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.directCall, 'directSTDReturn/iframeSTD-ApplicationLvl', {
			args
		});
	};
	async getCurrentProductNodesConfiguration(): Promise<ViewerComponent.INative_getCurrentProductNodesConfiguration_response> {
		return await this.createRequest(this.getCurrentProductNodesConfiguration, 'onCurrentProductNodesConfigurationGet', {});
	};
	async getCurrentProductNodesScreenPosition(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.getCurrentProductNodesScreenPosition, 'onCurrentProductNodesScreenPositionGet', {});
	};
	async updateProductNodesInstances(
		desc: {
			readonly localIdsToRemove: number[];
			readonly nodesToAdd: {
				readonly localId: number;
				readonly parentLocalId: number;
				readonly projectCode: string;
				readonly SKU: string;
			}[];
		}
	): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.updateProductNodesInstances, 'onSuccess', {
			localIdsToRemove: desc.localIdsToRemove,
			nodesToAdd: desc.nodesToAdd,
		});
	};
	async setPlannerFixedPositions(
		desc: Record<string, {
			readonly positions: Record<string, {
				position: ViewerComponent.TVector3;
				rotationaxis?: ViewerComponent.TVector3;
				rotationAngle?: Number;
			}>
		}>
	): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.setPlannerFixedPositions, 'onSuccess', {
			sets: desc
		});
	};
	async setPlannerDraggingGroup({localIds}: { localIds: number[] }): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.setPlannerDraggingGroup, 'onSuccess', {
			localIds
		});
	};
	async enableKeyboardTrackingMode(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.enableKeyboardTrackingMode, 'onSuccess', {});
	};
	async disableKeyboardTrackingMode(): Promise<ViewerComponent.ICallResponse> {
		return await this.createRequest(this.disableKeyboardTrackingMode, 'onSuccess', {});
	};
// async getARCompatibility() : Promise<ViewerComponent.ICallResponse> {
// 	//404 DOCUMENTATION NOT FOUND
// };
// async getARAvailability() : Promise<ViewerComponent.ICallResponse> {
// // 	//404 DOCUMENTATION NOT FOUND
// };
// async showScrollWithoutCtrlContainer(, viewpoint : number) : Promise<ViewerComponent.ICallResponse> {
// 	//404 DOCUMENTATION NOT FOUND
// };
// async showScrollWithoutCtrlContainer() : Promise<ViewerComponent.ICallResponse> {
// 	//404 DOCUMENTATION NOT FOUND
// };
// async hideScrollWithoutCtrlContainer() : Promise<ViewerComponent.ICallResponse> {
// 	//404 DOCUMENTATION NOT FOUND
// };

		static createPositionMatrix(
		x: number,
		y: number,
		z: number,
		rotationDeg: number
	) {
		const matrix = mat4.identity(mat4.create());
		mat4.translate(matrix, matrix, [x, y, z]);
		if (rotationDeg) {
			mat4.rotateY(matrix, matrix, STD.math.degToRad(rotationDeg));
		}
		return (<ViewerComponent.TMatrix>matrix);
	};
	static mtx(
		position: ViewerComponent.TVector3,
		scale: ViewerComponent.TVector3,
		rotation: number
	) {
		const m = mat4.create();
		const p = this.createPositionMatrix(
			position[0],
			position[1],
			position[2],
			rotation
		);
		const s = vec3.fromValues(scale[0], scale[1], scale[2]);
		mat4.scale(m, p, s);
		return (<ViewerComponent.TMatrix>m);
	};
	static positionFromMatrix(matrix: ViewerComponent.TMatrix) {
		const translation = vec3.create();
		mat4.getTranslation(translation, matrix);
		const scale = vec3.create();
		mat4.getScaling(scale, matrix);
		const q = quat.create();
		mat4.getRotation(q, matrix);
		const x = q[0], y = q[1], z = q[2], w = q[3];
		const sinr_cosp = 2 * (w * x + y * z);
		const cosr_cosp = 1 - 2 * (x * x + y * y);
		const rotX = Math.atan2(sinr_cosp, cosr_cosp);
		let sinp = 2 * (w * y - z * x);
		sinp = Math.max(-1, Math.min(1, sinp)); // clamp
		const rotY = Math.asin(sinp);
		const siny_cosp = 2 * (w * z + x * y);
		const cosy_cosp = 1 - 2 * (y * y + z * z);
		const rotZ = Math.atan2(siny_cosp, cosy_cosp);
		return {
			position: {
				x: translation[0],
				y: translation[1],
				z: translation[2]
			},
			scale: {
				x: scale[0],
				y: scale[1],
				z: scale[2]
			},
			rotation: {
				x: STD.math.radToDeg(rotX),
				y: STD.math.radToDeg(rotY),
				z: STD.math.radToDeg(rotZ)
			}
		};
	};
	mtx = ViewerComponent.mtx.bind(ViewerComponent);
	isLoaded() {
		return (!!this.viewerWindow && (this.loadingDetails.progress === 1));
	};
	private updateTarget() {
		const ld = this.loadingDetails;
		const ls = ViewerComponent.TLoadingStates;
		if (ld.state !== ls.void && ld.state !== ls.projectLoaded) {
			this.loadingPpack.reject(new Error('aborted'));
		}
		if (ld.state !== ls.void) {
			this.loadingPpack = STD.misc.createPromisePack<void>();
		}
		ld.state = ls.iframeLoading;
		this.viewerDOM.setAttribute(
			this.viewerDOM.tagName.toLowerCase() === 'object' ? 'data' : 'src',
			this.targetUrl
		);
	};
	async awaitLoading() {
		await this.loadingPpack.promise;
	};
	async batch(
		desc: ViewerComponent.IBatchDescriptor
	): Promise<(ViewerComponent.ICallResponse | ViewerComponent.ProductInstance[] | void[])[]> {
		let error: Error | unknown | null = null;
		const responses: (ViewerComponent.ICallResponse | ViewerComponent.ProductInstance[])[] = [];
		await this.freezeRendering({keepCount: true});
		for (const [method, parameters] of desc) {
			try {
				responses.push(<any>await (<any>this)[method](<any>parameters));
			} catch (e) {
				error = e;
				break;
			}
		}
		await this.unfreezeRendering({keepCount: true});
		if (error) {
			throw error;
		}
		return (responses);
	};
	async mpaengine_deleteProduct(
		productsInstances: ViewerComponent.IMPAEngineDeleteProduct
	) {
		const toDelete: number[] = [];
		const ppack = STD.misc.createPromisePack<void>();
		const deletePromises: Promise<void>[] = productsInstances.map(productInstance => {
			return productInstance.deleteBatch(
				() => {
					toDelete.push(productInstance.localId);
					return ppack.promise;
				}
			);
		});
		await this.removeProductNodesInstances(toDelete);
		ppack.resolve();
		return Promise.all(deletePromises);
	};
	createEmptyNodeDescriptor(
		localId: string,
		parentLocalId: string,
		matrix: 0 | 1 | ViewerComponent.TMatrix
	): ViewerComponent.Product.INode {
		return {
			SKU: this.component_configuration.emptyProjectSKU,
			projectCode: this.component_configuration.emptyProjectCode,
			localId: localId,
			parentLocalId: parentLocalId,
			snappable: false,
			fixedPositionSetIds: null,
			draggable: false,
			doNotListAsProductComponent: true,
			matrix: matrix === 0 ?
				this.mtx([0, 0, 0], [0, 0, 0], 0) :
				matrix === 1 ?
					this.mtx([0, 0, 0], [1, 1, 1], 0) : matrix
		};
	};
	transformMeshInstanceTexture_helper({
																				image,
																				zone
																			}: ViewerComponent.IExtended_transformMeshInstanceTextureHelper_descriptor): ViewerComponent.IExtended_transformMeshInstanceTextureHelper_response {
		let size;
		let baseOffsetX;
		let baseOffsetY;
		const kImg = image.resolutionX / image.resolutionY;
		const kWall = zone.width / zone.height;
		let kMin;
		let physicalImageWidth;
		let physicalImageHeight;
		if (kImg > kWall) {
			size = zone.width * image.scale;
			kMin = image.resolutionY;
			baseOffsetX = size / 2;
			baseOffsetY = (size * ((image.resolutionY / image.resolutionX))) / 2;
			physicalImageWidth = size;
			physicalImageHeight = size / kImg;
		} else {
			size = zone.height * image.scale;
			kMin = image.resolutionX;
			baseOffsetX = (size * ((image.resolutionX / image.resolutionY))) / 2;
			baseOffsetY = size / 2;
			physicalImageWidth = size * kImg;
			physicalImageHeight = size;
		}
		void baseOffsetX; //used to align left or right (center used currently)
		void baseOffsetY; //used to align top  or bottom(center used currently)
		const transform = {
			translation: <ViewerComponent.TVector2>[
				(0.5 - (zone.width / 2)) - image.left,
				(0.5 - (zone.height / 2)) - image.up
			],
			scaling: <ViewerComponent.TVector2>[
				(image.resolutionY / kMin) / size,
				(image.resolutionX / kMin) / size
			],
			rotationCenter: <ViewerComponent.TVector2>[
				(zone.width / 2) + image.left,
				(zone.height / 2) + image.up
			],
			rotation: image.rotation,
			scaleX: zone.width,
			scaleY: zone.height
		};
		// if (image.rotation) {
		// 	const tmp = MPASTD.math.computeSizeAfterRotation(
		// 		physicalImageWidth,
		// 		physicalImageHeight,
		// 		image.rotation
		// 	);
		// 	physicalImageWidth  = tmp.x;
		// 	physicalImageHeight = tmp.z;
		// }
		const w2 = physicalImageWidth / 2;
		const h2 = physicalImageHeight / 2;
		const origin = {
			x: w2,
			y: h2
		};
		const newOrigin = {
			x: origin.x + image.left,
			y: origin.y + image.up
		};
		const from = {
			x: newOrigin.x - w2,
			y: newOrigin.y - h2
		};
		const to = {
			x: newOrigin.x + w2,
			y: newOrigin.y + h2
		};
		const visiblePhysicalImageWidth = (
			((to.x <= zone.width) ? to.x : zone.width) -
			((from.x >= 0) ? from.x : 0)
		);
		const visiblePhysicalImageHeight = (
			((to.y <= zone.height) ? to.y : zone.height) -
			((from.y >= 0) ? from.y : 0)
		);
		return {
			visiblePhysicalImageWidth,
			visiblePhysicalImageHeight,
			newOrigin,
			from,
			to,
			patchPhysicalWidth: zone.width,
			patchPhysicalHeight: zone.height,
			transformMeshInstanceTexture: {
				name: zone.mesh,
				nodeLocalId: zone.nodeLocalId,
				transform
			}
		};
	};
	createCategory(descriptor: ViewerComponent.ProductCategory.IDescriptor) {
		const category = new ViewerComponent.ProductCategory(descriptor);
		this.categories.push(category);
		return category;
	};
	enableIframeInteractions() {
		if (this.isInteractionsEnabled) {
			return;
		}
		this.component_DOM.classList.remove('iframeInteractionsDisabled');
		this.isInteractionsEnabled = true;
	};
	disableIframeInteractions() {
		if (!this.isInteractionsEnabled) {
			return;
		}
		this.component_DOM.classList.add('iframeInteractionsDisabled');
		this.isInteractionsEnabled = false;
	};
	getLocalId() {
		return (this.safe.value.baseLocalId++);
	};
	override async updateValue(
		newValue: NS.Value,
		_defaultValueMarker?: Symbol
	) {
		void newValue;
		void _defaultValueMarker;
		const oldValue = this.value;
		void oldValue;
		await this.dispatchEvent('changeViewer', {
			subType: 'start'
		});
		await super.updateValue(newValue, _defaultValueMarker);
		this.updateTarget();
		await this.awaitLoading();
		await this.dispatchEvent('changeViewer', {
			subType: 'end'
		});
	};
	serialize() {
		if (!this.value) {
			return null;
		}
		return {
			baseLocalId: this.value.baseLocalId,
			viewerCode: this.value.viewerCode,
			productInstances: Array.from(this.value.productInstances).map(x => x.serialize()),
			configurationCode: this.value.configurationCode
		};
	};
	async applyOption(option: ViewerComponent.TOption, nodeLocalIds?: number[] | null) {
		if (option.type === 'group') {
			if ('groupName' in option) {
				await this.setMaterialsGroup(
					{
						groupName: option.groupName,
						configurationName: option.configurationName,
						nodeLocalIds: nodeLocalIds ? nodeLocalIds : option.nodeLocalIds
					}
				);
			} else {
				await this.setMaterialsGroup(
					{
						groupCode: option.groupCode,
						configurationCode: option.configurationCode,
						nodeLocalIds: nodeLocalIds ? nodeLocalIds : option.nodeLocalIds
					}
				);
			}
		} else if (option.type === 'groups') {
			await this.setMaterialsGroups(
				{
					values: option.values,
					nodeLocalIds: nodeLocalIds ? nodeLocalIds : option.nodeLocalIds
				}
			);
		} else if (option.type === 'materials') {
			await this.setMaterials(
				{
					materials: option.materials,
					nodeLocalIds: nodeLocalIds ? nodeLocalIds : option.nodeLocalIds
				}
			);
		} else if (option.type === 'preset') {
			if ('name' in option) {
				await this.setPreset({
					name: option.name,
					nodeLocalIds: nodeLocalIds ? nodeLocalIds : option.nodeLocalIds
				});
			} else {
				await this.setPreset({
					SKU: option.SKU,
					nodeLocalIds: nodeLocalIds ? nodeLocalIds : option.nodeLocalIds
				});
			}
		}
	};
	

	async updateDraggingGroup() {
		const localIds = Array.from(ViewerComponent.self.currentSelectedInstances).map(instance => instance.currentLocalId!);
		await ViewerComponent.self.setPlannerDraggingGroup({localIds});
	};
	async unselectAll(updateDraggingGroup = true) {
		if (this.currentSelectedInstances.size === 0)
			return;
		await Promise.all(
			Array.from(this.currentSelectedInstances).map(instance => instance.unselect(false))
		);
		if (updateDraggingGroup)
			await this.updateDraggingGroup();
	};
	private async onMeshSelection(ev: STD.events.Event<ViewerComponent, any>) {
		const details = <ViewerComponent.INative_meshSelection_event>ev.details;
		const instance = Array.from(this.safe.value.productInstances).
		find(instance => instance.localsIds.includes(details.nodeLocalId));
		if (!instance) {
			await this.unselectAll();
			return;
		}
		const multiSelection = app.shortcutsManager.matchShortcut('Control');
		if (this.currentSelectedInstances.has(instance)) {
			if (multiSelection) {
				await instance.unselect();
				return;
			}
			await this.unselectAll();
		} else if (!multiSelection) {
			await this.unselectAll();
		}
		const mouseButtonRaw = ev.details.button;
		const mouseButton = (
			(mouseButtonRaw !== undefined) ? (
				(mouseButtonRaw === 0) ? 'left' :
					(mouseButtonRaw === 1) ? 'wheel' :
						(mouseButtonRaw === 2) ? 'right' :
							null
			) : null
		);
		await instance.select(mouseButton, details.meshName, details.nodeLocalId);
	};
	private onProductNodeInstanceTrackingUpdate(ev: STD.events.Event<ViewerComponent, any>) {
		const details = <ViewerComponent.INative_productNodeInstanceTrackingUpdate_event>ev.details;
		const instances = Array.from(this.safe.value.productInstances);
		rootFor : for (const node of details.nodes) {
			let instance = instances.find(x => x.localId === node.localId);
			if (!instance) {
				instance = instances.find(x => x.localsIds.includes(node.localId));
				if (!instance) {
					continue rootFor;
				}
			}
			instance.position.UI.x = node.x;
			instance.position.UI.y = node.y;
			instance.position.UI.z = node.z;
		}
	};
	private onProductNodeInstanceDraggingStopped(event: STD.events.Event<ViewerComponent, any>) {
		const details = <ViewerComponent.INative_productNodeInstanceDraggingStop_event>event.details;
		const instance = Array.from(this.safe.value.productInstances).find(x => x.localId === details.nodeLocalId);
		if (!instance) {
			return;
		}
		const s = instance.position.scene;
		s.x = details.position[0];
		s.y = details.position[1];
		s.z = details.position[2];
	};
	private onProductInDragg(ev: STD.events.Event<ViewerComponent, any>) {
		const d = ev.details;
		const p = ev.details.position;
		for (const instance of this.safe.value.productInstances) {
			if (instance.localId !== d.nodeLocalId) {
				continue;
			}
			const s = instance.position.scene;
			s.x = p[0];
			s.y = p[1];
			s.z = p[2];
		}
	};
	constructor(
		descriptor: ConstructorParameters<typeof CM.DOMComponent>[0],
		configuration: IViewerComponentConfiguration
	) {
		super(descriptor, Object.assign(
			{
				'@preventDefaultDisplayNone': true
			},
			configuration
		));
		if (ViewerComponent.instance) {
			this.component_throwUserError('Viewer already instanced!', {});
		}
		ViewerComponent.instance = this;
		const appComponent = this.component_getRoot();
		if (!(appComponent instanceof window.MPAEngineNS.components.Application.Component)) {
			throw STD.errors.user(
				'Root component must be an instance of an application component!',
				{root: appComponent}
			);
		}
		this.application = appComponent;
		const tag = 'object';
		// const tag = (
		// 	(this.application.isOnProd && !this.application.settings.isJS) ? 'object' :
		// 		'iframe'
		// );
		this.viewerDOM = <HTMLObjectElement | HTMLIFrameElement>STD.DOM.factory({
			tag,
			events: {
				load: this.onIframeLoad.bind(this)
			}
		});
		this.component_DOM = <HTMLDivElement>STD.DOM.factory({
			childs: [
				this.viewerDOM
			]
		});
		this.productsExtensionById = configuration.productsExtensionById || {};
		this.addEventListener('message', this.onMeshSelection.bind(this), {
			listener: this,
			subType: 'onMeshSelection'
		});
		this.addEventListener('message', this.onProductNodeInstanceTrackingUpdate.bind(this), {
			listener: this,
			subType: 'onProductNodeInstanceTrackingUpdate',
		});
		this.addEventListener('message', this.onProductNodeInstanceDraggingStopped.bind(this), {
			listener: this,
			subType: 'onProductNodeInstanceDraggingStopped',
		});
		this.addEventListener('message', this.onProductInDragg.bind(this), {
			listener: this,
			subType: 'onProductNodeInstanceDraggingUpdate'
		});
		this.addEventListener('message', this.onKeyPress.bind(this), {
			listener: this,
			subType: 'onKeyPress'
		});
		this.addEventListener('message', this.onKeyRelease.bind(this), {
			listener: this,
			subType: 'onKeyRelease'
		});
	};
};
namespace ViewerComponent {
	// section interfaces
export enum TLoadingStates {
	void = 0,
	iframeLoading = 1,
	iframeLoaded = 2,
	projectLoading = 3,
	projectLoaded = 4
};
export type TPBRContent = (
	'albedo' |
	'albedo.specular' |
	'albedo.specularIntensity' |
	'roughness' |
	'roughness.sheen' |
	'roughness.sheenIntensity' |
	'roughness.sheenRoughness' |
	'metalness' |
	'metalness.anisotropy' |
	'metalness.anisotropyIntensity' |
	'metalness.anisotropyRoughness' |
	'metalness.anisotropyRotation' |
	'opacity' |
	'normalDeformation' |
	'normalDeformationIntensity' |
	'normalDeformation2' |
	'normalDeformationIntensity2' |
	'translucency' |
	'emissive' |
	'emissiveIntensity' |
	'emissive.glowColor' |
	'emissive.glowOpacity' |
	'emissive.glowSaturation' |
	'iridescence' |
	'coatingIntensity' |
	'coatingThickness' |
	'coatingRoughness' |
	'occlusionAmbient' |
	'occlusionLight'
	);
export interface ICall {
	readonly action: string;
	readonly id: number | undefined | null;
	[key: string]: unknown;
};
export interface ICallResponse extends ICall {
	readonly callAction: string;
};
export type TVector2 = [number, number];
export type TVector3 = [number, number, number];
export type TMatrix = [
	number, number, number, number, number, number, number, number,
	number, number, number, number, number, number, number, number
];
export interface ISize {
	width: number;
	depth: number;
	height: number;
};
export enum TErrorsCode {
	undefinedAction = 300,
	uninitializedAPI = 301,
	viewerNotLoaded = 302,
	invalidScreenshot = 303,
	incorrectParameter = 304,
	alreadyLoadedViewer = 305,
	alreadyStartedViewer = 306,
	transactionAlreadyInUse = 310,
	transaction = 311,
	preset = 312,
	presetNotFound = 315,
	configurationNotFound = 316,
	materialsGroupsConfiguration = 317,
	unsupportedLanguage = 318,
	annotations = 319
};
export interface ICameraControl {
	readonly settings: {
		readonly targetRange: number;
		readonly xRotationMax: number;
		readonly xRotationMin: number;
		readonly yRotationMax: number;
		readonly yRotationMin: number;
	};
	readonly type: number;
};
export interface ICamera {
	readonly position: TVector3;
	readonly target: TVector3;
	readonly up: TVector3;
	readonly fov: number;
	readonly cameraControl: ICameraControl;
	readonly transitionTime?: number;
	readonly checkCurrentPosition?: boolean;
};
export type INative_setProductNodesInstancesMatrices_descriptor = {
	localId: number;
	matrix: TMatrix;
}[];
// section events
export interface INative_stateChange_event extends ICallResponse {
	readonly action: 'onStateChange';
	readonly emersyaContext: string;
	readonly id: null;
	readonly state: {
		readonly arCompatibility: boolean;
		readonly loadingProgress: number;
		readonly viewerState: 'started' | 'loaded';
		readonly viewerType: 'webgl';
		readonly webglStatus: 'webgl';
		readonly version: {
			readonly actualProject: number;
			readonly actualViewer: number;
			readonly loadedFromConfiguration: boolean;
			readonly routeProject: number;
			readonly routeViewer: number;
		};
	};
};
export interface INative_meshHighlight_event extends ICallResponse {
	readonly action: 'onMeshHighlight';
	readonly emersyaContext: string;
	readonly configurableMaterialName: string[];
	readonly configurableMaterialsGroupName: string[];
	readonly meshName: string[];
	readonly nodeIds: [];
	readonly nodeLocalIds: [];
};
export interface INative_mouseTrackingUpdate_event extends ICallResponse {
	readonly action: 'onMouseTrackingUpdate';
	readonly emersyaContext: string;
	readonly position: TVector3 | null;
	readonly uv: TVector2 | null;
	readonly pressed: boolean;
	readonly polylistName: string;
	readonly materialTreeName: string;
	readonly materialTreesGroupName: string;
	readonly nodeId: number | null;
	readonly nodeLocalId: number | null;
};
export interface INative_meshSelection_event extends ICallResponse {
	readonly action: 'onMeshSelection';
	readonly configurableMaterialName: string[];
	readonly configurableMaterialsGroupName: string[];
	readonly emersyaContext: string;
	readonly meshName: string[];
	readonly nodeId: number;
	readonly nodeLocalId: number;
	readonly position: TVector3;
	readonly uv: TVector2;
};
export interface INative_productNodeInstanceTrackingUpdate_event extends ICallResponse {
	readonly action: 'onProductNodeInstanceTrackingUpdate';
	readonly emersyaContext: string;
	readonly nodes: {
		readonly displayed: boolean;
		readonly localId: number;
		readonly onViewport: boolean;
		readonly visible: undefined;
		readonly x: number;
		readonly y: number;
		readonly z: number;
	}[];
};
export interface INative_productNodeInstanceDraggingStop_event extends ICallResponse {
	readonly action: 'onProductNodeInstanceDraggingStopped';
	readonly emersyaContext: string;
	readonly position: [number, number, number];
	readonly nodeLocalId: number;
};
// section methods parameters
export interface INative_addProductNodeInstance_descriptor {
	readonly SKU: string;
	readonly projectCode: string;
	readonly localId: number;
	readonly parentLocalId: number;
	readonly matrix: TMatrix;
	readonly tracked?: boolean;
	readonly draggable?: boolean;
	readonly fixedPositionSetIds?: string[] | null;
	configuration?: {
		readonly presetName: string;
	} | /** @Deprecated **/ {
		readonly meshesMaterials: {
			readonly mesh: string;
			readonly materialVariation: string;
		}[];
	} | /** @Deprecated **/ {
		readonly materials: {
			readonly configurableMaterial: string;
			readonly materialVariation: string;
		}[];
	} | /** @Deprecated **/ {
		readonly materialsGroups: {
			readonly groupName: string;
			readonly configurationName: string;
		}[];
	};
};
export type INative_setCamera_descriptor =
	Partial<ICamera>
	& Omit<ICamera, 'fov' | 'transitionTime' | 'checkCurrentPosition' | 'cameraControl'>
	;
export interface INative_getScreenshot_descriptor {
	/** @deprecated **/ readonly takeBackground?: boolean;
	readonly transparentBackground?: boolean;
	readonly width: number;
	readonly height: number;
	readonly camera?: INative_setCamera_descriptor;
};
export type INative_getScreenshots_descriptor = Omit<INative_getScreenshot_descriptor, 'screenshots'> & ({
	readonly  cameras: INative_setCamera_descriptor[];
} | {
	readonly  viewpoints: string[];
});
export interface INative_setZoomProportion_descriptor {
	readonly value: number;
	readonly transitionTime?: number;
};
export interface INative_updateAnnotationPin_descriptor {
	readonly annotationId: number;
	readonly title: string;
	readonly description: string;
};
export interface INative_addAnnotationPin_descriptor {
	readonly annotationId: number;
	readonly number: number;
	readonly title: string;
	readonly description: string;
	readonly onNextClick?: boolean;
	readonly nodeId?: number;
	readonly localId?: number;
	readonly position?: [number, number, number];
};
export interface INative_removeAnnotationPin_descriptor {
	readonly annotationId: number;
};
export interface INative_setMaterials_descriptor {
	readonly nodeLocalIds?: number[];
	readonly materials: {
		readonly configurableMaterial: string;
		readonly materialVariation: string;
		readonly nodeLocalIds?: number;
	}[];
};
export interface INative_setMaterialsGroups_descriptor {
	readonly nodeLocalIds?: number[];
	readonly values: (INative_group_byName | INative_group_byCode)[];
};
export interface INative_updateCustomTexture_descriptor {
	readonly configurableMaterial: string;
	readonly materialVariation: string;
	readonly image: string;
	readonly contents: TPBRContent[];
};
export type INative_transformMeshInstancesTextures_descriptor = {
	readonly name: string;
	readonly nodeLocalId: number;
	readonly transform: {
		readonly translation: [number, number, number?];
		readonly scaling: [number, number, number?];
		readonly rotationCenter: [number, number, number?];
		readonly rotation: number;
		readonly scaleX: number;
		readonly scaleY: number;
		readonly offsetX?: number;
		readonly offsetY?: number;
	};
};
export interface INative_setMaterialsGroup_base_descriptor {
	readonly nodeLocalIds?: number[];
	readonly transitionTime?: number;
}
export interface INative_group_byCode {
	readonly groupCode: string;
	readonly configurationCode: string;
};
export interface INative_group_byName {
	readonly groupName: string;
	readonly configurationName: string;
};
export interface INative_setMaterialsGroup_byCode_descriptor extends INative_setMaterialsGroup_base_descriptor, INative_group_byCode {
};
export interface INative_setMaterialsGroup_byName_descriptor extends INative_setMaterialsGroup_base_descriptor, INative_group_byName {
};
export type TNative_setMaterialsGroup_descriptor =
	INative_setMaterialsGroup_byCode_descriptor |
	INative_setMaterialsGroup_byName_descriptor
	;
export interface INative_setPreset_descriptor_byName {
	readonly nodeLocalIds?: number[];
	readonly name: string;
};
export interface INative_setPreset_descriptor_bySKU {
	readonly nodeLocalIds?: number[];
	readonly SKU: string;
};
export type TNative_setPreset_descriptor = INative_setPreset_descriptor_byName | INative_setPreset_descriptor_bySKU;
export type INative_updateMeshInstanceCustomTextures_descriptor = {
	readonly name: string;
	readonly nodeLocalId: number;
	readonly image: string;
	readonly contents?: TPBRContent[];
};
export interface INative_freezeRendering_descriptor {
	readonly keepCount?: boolean;
};
export interface INative_unfreezeRendering_descriptor {
	readonly keepCount?: boolean;
};
export interface INative_setProductNodesInstancesDraggable_descriptor {
	readonly nodes: {
		localId: number;
		draggable: boolean;
	}[];
};
// section methods output
export interface INative_getScreenshot_response extends ICallResponse {
	readonly screenshot: string;
};
export interface INative_getScreenshots_response extends ICallResponse {
	readonly screenshots: string[];
};
export interface INative_getCamera_response extends ICallResponse {
	readonly camera: ICamera;
};
export interface INative_saveConfiguration_response extends ICallResponse {
	readonly code: string;
	readonly metadataPath?: string;
	readonly configurableShortLink?: string;
	readonly shortLink?: string;
};
export interface INative_getCurrentMaterials_response extends ICallResponse {
	readonly materialSettingsConfiguration: {
		readonly materialTree: string;
		readonly materialSettings: string;
	}[];
};
export interface INative_getPlannerUsableProjects_response extends ICallResponse {
	readonly callAction: 'getPlannerUsableProjects';
	readonly data: {
		readonly name: string;
		readonly projects: {
			readonly code: string;
			readonly id: string;
			readonly productNodes: {
				readonly SKU?: string;
				readonly previewPath?: string;
				readonly name?: string;
			}[];
		}[];
	}[];
};
export interface INative_startProductNodeDrop_node_descriptor {
	readonly SKU: string;
	readonly projectCode: string;
	readonly localId: number;
	readonly parentLocalId: number;
	readonly snappable?: boolean;
	readonly fixedPositionSetIds?: string[] | null;
	// fixedPositionSetIds : [] | null;
};
export interface INative_getCurrentProductNodesConfiguration_response extends ICallResponse {
	readonly callAction: 'getPlannerUsableProjects';
	readonly productNodes: {
		readonly SKU: string;
		readonly boundingBox: {
			readonly center: TVector3;
			readonly maxX: number;
			readonly maxY: number;
			readonly maxZ: number;
			readonly minX: number;
			readonly minY: number;
			readonly minZ: number;
			readonly radius: number;
		};
		readonly localId: number;
		readonly matrix: TMatrix;
		readonly parentLocalId: number;
		readonly position: TVector3;
		readonly projectCode: string;
		readonly rotationAngle: number;
		readonly rotationAxis: TVector3;
		readonly scaling: TVector3;
	}[];
};

		export interface IMPAEngineInstantiateProduct {
		product: ViewerComponent.Product;
		rootLocalId: number;
		metadata: Record<string, unknown> | null;
		matrixOverride?: Record<string, ViewerComponent.TMatrix>;
		basketUseDynamicTitleBinding: boolean;
	};
	export type IMPAEngineDeleteProduct = ViewerComponent.ProductInstance[];
	export type IBatchDescriptor = (
		['addProductNodesInstances', ViewerComponent.INative_addProductNodeInstance_descriptor[]] |
		['setProductNodesInstancesMatrices', ViewerComponent.INative_setProductNodesInstancesMatrices_descriptor] |
		['transformMeshInstancesTextures', ViewerComponent.INative_transformMeshInstancesTextures_descriptor[]] |
		['removeProductNodesInstances', number[]] |
		['setMaterials', ViewerComponent.INative_setMaterials_descriptor] |
		['setMaterialsGroup', ViewerComponent.TNative_setMaterialsGroup_descriptor] |
		['updateMeshInstanceCustomTextures', ViewerComponent.INative_updateMeshInstanceCustomTextures_descriptor[]] |
		['setMaterialsGroups', ViewerComponent.INative_setMaterialsGroups_descriptor] |
		['mpaengine_instantiateProduct', IMPAEngineInstantiateProduct[]] |
		['mpaengine_deleteProduct', IMPAEngineDeleteProduct]
		)[];
	export interface ISelectionEventDetails {
		productInstance: ProductInstance;
		isEnabled: boolean;
	};
	export interface IExtended_transformMeshInstanceTextureHelper_descriptor {
		readonly zone: {
			readonly width: number;
			readonly height: number;
			readonly mesh: string;
			readonly nodeLocalId: number;
		};
		readonly image: {
			readonly resolutionX: number;
			readonly resolutionY: number;
			readonly left: number;
			readonly up: number;
			readonly scale: number;
			readonly rotation: number;
		};
	};
	export interface IExtended_transformMeshInstanceTextureHelper_response {
		visiblePhysicalImageWidth: number;
		visiblePhysicalImageHeight: number;
		patchPhysicalWidth: number;
		patchPhysicalHeight: number;
		newOrigin: { x: number; y: number };
		from: { x: number; y: number };
		to: { x: number; y: number };
		transformMeshInstanceTexture: ViewerComponent.INative_transformMeshInstancesTextures_descriptor;
	};
	export interface IProductPreset {
		readonly id: string;
		readonly name: STD.translationsManager.TranslationMapping;
		readonly iconURL: string;
		// readonly groups: Record<string, string>;
		readonly option: TOption;
		readonly pricingInfo: BasketNS.Option.IDescriptor['pricingInfo'];
	};
	export interface IProductExtension {
		name?: STD.translationsManager.TranslationMapping;
		description?: STD.translationsManager.TranslationMapping;
		/** If defined as record, the key used to retrieve the default is got by resolveSpecific so it must be defined. **/
		pricing?: BasketNS.Pricing.TPricing | null;
		/** If defined as record, the key used to retrieve the default is got by resolveSpecific so it must be defined. **/
		readonly defaultPreset?: string | null | Record<string, string>;
		readonly presets?: IProductPreset[];
		readonly size?: {
			x: number;
			y: number;
			z: number;
		};
		readonly origin?: {
			x: number;
			y: number;
			z: number;
		};
		readonly customImages?: ({
			id: string;
			title: STD.translationsManager.TranslationMapping;
			width: number;
			height: number;
			mesh: string;
			getLocalId?: (this: ProductInstance, customImageId: string) => number;
			getSizeX?: (this: ProductInstance, customImageId: string) => number;
			getSizeY?: (this: ProductInstance, customImageId: string) => number;
			material: {
				name: string;
				configuration: {
					enabled: string;
					disabled: string;
				};
			};
			// pricingInfo : {
			// 	readonly useSurfaceAsVolume : boolean;
			// 	readonly pricing : BasketNS.Pricing.TPricing | null;
			// 	readonly volumeComputation? : {
			// 		readonly unit : 'sqm' | 'm.height' | 'm.width';
			// 		readonly target : 'image' | 'patch';
			// 		readonly roundingStrategy : 'floor' | 'round' | 'ceil' | 'none';
			// 	};
			// } | null;
			// BasketNS.Pricing.TPricing | null
			// pricing : {
			// 	readonly useSurfaceAsVolume : boolean;
			// 	/** If defined as record, the key used to retrieve the default is got by resolveSpecific so it must be defined. **/
			// 	readonly price : BasketNS.TPricesBySpecific;
			// 	readonly taxes : BasketNS.ITaxSerialized[];
			// 	readonly volumeComputation? : {
			// 		readonly unit : 'sqm' | 'm.height' | 'm.width';
			// 		readonly target : 'image' | 'patch';
			// 		readonly roundingStrategy : 'floor' | 'round' | 'ceil' | 'none';
			// 	};
			// } | null;
		} & Pick<BasketNS.IOptionCustomization<true>, 'pricingInfo'>)[];
		disabledButtons?: string[]; // List of planner menu disabled buttons if the menu used
		onPresetChanged?: (this: ProductInstance) => Promise<void> | void;
		resolveSpecificKey?: (
			this: ProductInstance | Product,
			what: 'pricing' | 'quantity' | 'customImagesPricing' | 'defaultPreset'
		) => Promise<string> | string;
		readonly quantity: {
			readonly min: number | null;
			readonly max: number | null;
			readonly correlatedComponents: string[];
		} | null;
		/**
		 * Called before configuratorGet call to prepare the configurator to the product instance
		 * Only used if configuratorGet is used and even in this case it's optional
		 * Should returns a function with type "(Emersya.Viewer.Planner.ProductInstance) => void | Promise<void>"
		 **/
		readonly configuratorPrepare?: CM.PropertyOperation.TOperation | null; // Only used if configuratorGet is used
		/**
		 * Used to retrieve the component in charge of configuring the product
		 * Should returns a function with type "(Emersya.Viewer.Planner.ProductInstance) => (DOMComponent | TComponentPath | TRichComponentPath) | Promise<DOMComponent | TComponentPath | TRichComponentPath>"
		 **/
		readonly configuratorGet?: CM.PropertyOperation.TOperation | null;
		/**
		 * Called after configuratorGet call to clean the configurator
		 * Only used if configuratorGet is used and even in this case it's optional
		 * Should returns a function with type "(Emersya.Viewer.Planner.ProductInstance) => void | Promise<void>"
		 **/
		readonly configuratorClean?: CM.PropertyOperation.TOperation | null; // Only used if configuratorGet is used
	};
	export type TPositionCommand = HistoryManagerNS.Command<'viewer/position', {
		localId: number;
		from: ProductInstance['position']['scene'];
		to: ProductInstance['position']['scene'];
	}>;
	export interface IOption {
		readonly type: string;
		localIds?: number[];
	};
	export interface IOptionMaterial extends IOption {
		readonly type: 'materials';
		readonly nodeLocalIds?: number[];
		readonly materials: {
			readonly configurableMaterial: string;
			readonly materialVariation: string;
			readonly nodeLocalIds?: number;
		}[];
	};
	export interface IOptionGroup extends IOption {
		readonly type: 'group';
		readonly nodeLocalIds?: number[];
	};
	export interface IOptionGroupByName extends IOptionGroup, ViewerComponent.INative_group_byName {
		readonly groupName: string;
		readonly configurationName: string;
	};
	export interface IOptionGroupByCode extends IOptionGroup, ViewerComponent.INative_group_byCode {
		readonly groupCode: string;
		readonly configurationCode: string;
	};
	export interface IOptionGroups extends IOption {
		readonly type: 'groups';
		readonly nodeLocalIds?: number[];
		readonly values: (ViewerComponent.INative_group_byName | ViewerComponent.INative_group_byCode)[];
	};
	export interface IOptionPresetByName extends IOption {
		readonly type: 'preset';
		readonly nodeLocalIds?: number[];
		readonly name: string;
	};
	export interface IOptionPresetBySKU extends IOption {
		readonly type: 'preset';
		readonly nodeLocalIds?: number[];
		readonly SKU: string;
	};
	export type TOptionPreset = IOptionPresetByName | IOptionPresetBySKU;
	export type TOption = IOptionMaterial | IOptionGroupByName | IOptionGroupByCode | IOptionGroups | TOptionPreset;
		export class ProductCategory {
		name: string;
		products: ViewerComponent.Product[] = [];
		createProduct(desc: ViewerComponent.Product.IDescriptor) {
			const product = new Product(desc, this);
			this.products.push(product);
			return product;
		};
		readonly menuActions: Record<'edit' | 'rotate' | 'delete' | 'duplicate', boolean>;
		serialize(): ProductCategory.ISerialized {
			return {
				name: this.name,
				products: this.products.map(x => x.serialize())
			};
		};
		constructor(desc: ViewerComponent.ProductCategory.IDescriptor) {
			this.name = desc.name;
			this.menuActions = desc.menuActions;
			if (desc.products) {
				for (const productDescriptor of desc.products) {
					this.createProduct(productDescriptor);
				}
			}
		};
	};
	export namespace ProductCategory {
		export interface IDescriptor {
			readonly name: string;
			readonly products?: Product.IDescriptor[];
			readonly menuActions: Record<'edit' | 'rotate' | 'delete' | 'duplicate', boolean>;
		};
		export interface ISerialized {
			readonly name: string;
			readonly products: Product.ISerialized[];
		};
	}

		export class Product {
		thumbnailURL: string;
		category: ViewerComponent.ProductCategory;
		instances: Set<ViewerComponent.ProductInstance> = new Set;
		iconDOM: HTMLImageElement = new Image;
		extension: ViewerComponent.IProductExtension | null = null;
		name: string;
		SKU: string;
		nodes: Product.INode[];
		positionsSetId: string | null;
		getNameTranslationId() {
			if (this.name === 'Unnamed') {
				return STD.sessionId + '.productInstance.unnamed.name';
			}
			return STD.sessionId + '.productInstance.' + this.getId() + '.name';
		};
		getId() {
			return this.SKU;
		};
		toJSON() {
			return ({
				nodes: this.nodes,
				instances: Array.from(this.instances).map(x => x.serialize()),
				category: this.category.name
			});
		};
		concretizeProductNode(rootLocalId: number): {
			nodes: ViewerComponent.INative_addProductNodeInstance_descriptor[];
			mappings: Record<string, number>;
		} {
			const localsIdsInFunctionOfId: Record<string, number> = {
				root: rootLocalId
			};
			for (const nodeSchema of this.nodes) {
				const localId = app.viewer.getLocalId();
				localsIdsInFunctionOfId[nodeSchema.localId] = localId;
			}
			const nodes = this.nodes.map<ViewerComponent.INative_addProductNodeInstance_descriptor>(nodeSchema => Object.assign(
				{
					snappable: false,
					fixedPositionSetIds: null
				},
				nodeSchema,
				{
					localId: localsIdsInFunctionOfId[nodeSchema.localId],
					parentLocalId: (nodeSchema.parentLocalId === 'root') ?
						rootLocalId :
						localsIdsInFunctionOfId[nodeSchema.parentLocalId],
				}
			));
			const localRootNode = nodes[0];
			if (!localRootNode) {
				throw new Error;
			}
			if (this.positionsSetId) {
				(<STD.object.SetWritable<typeof localRootNode, 'fixedPositionSetIds'>>localRootNode).fixedPositionSetIds =
					[this.positionsSetId];
			}
			return {
				nodes,
				mappings: localsIdsInFunctionOfId
			};
		};
		generateBasketOptionCustomImagesGetter(
			instance: ProductInstance,
			CIExtensions: NonNullable<IProductExtension['customImages']>
		) {
			return () => {
				const out = new Set<BasketNS.IOptionCustomizationImage<false>>;
				let CIIdx = 0;
				for (const CIInfo of (instance.metadata.customImages || [])) {
					if (CIInfo.enabled && CIInfo.image) {
						const CIExtension = CIExtensions[CIIdx];
						if (!CIExtension) {
							throw STD.errors.library('', {});
						}
						const VI = CIInfo.viewerInfos;
						const volume = (
							(
								!CIExtension.pricingInfo ||
								!CIExtension.pricingInfo.volumeComputationMethod ||
								CIExtension.pricingInfo.volumeComputationMethod.boundTo === 'unit'
							) ? 1 : (() => {
								switch (CIExtension.pricingInfo.volumeComputationMethod.boundTo) {
									case 'surface-image-sqm':
										return VI.visiblePhysicalImageWidth * VI.visiblePhysicalImageHeight;
									case 'surface-image-m.height':
										return VI.visiblePhysicalImageHeight;
									case 'surface-image-m.width':
										return VI.visiblePhysicalImageWidth;
									case 'surface-patch-sqm':
										return VI.patchPhysicalWidth * VI.patchPhysicalHeight;
									case 'surface-patch-m.height':
										return VI.patchPhysicalHeight;
									case 'surface-patch-m.width':
										return VI.patchPhysicalWidth;
									default:
										throw new Error;
								}
							})()
						);
						// const price = CIExtension.pricing ?
						// 	(() => {
						// 		const p = CIExtension.pricing.price[ '*' ];
						// 		if (!p) {
						// 			throw STD.errors.user('');
						// 		}
						// 		return BasketNS.Price.from(
						// 			STD.misc.getDataInFunctionOfQuantity(p, volume),
						// 			taxes
						// 		);
						// 	})() :
						// 	BasketNS.Price.ofZero()
						// ;
						// next code is very old, have some inconsistencies because in new codebase image shouldnt be string!!!
						const imgAsString = (typeof <any>CIInfo.image === 'string') ? <string><any>CIInfo.image : CIInfo.image.src;
						out.add({
							volume,
							id: `image-${CIIdx}`,
							pricingInfo: !CIExtension.pricingInfo ? null : {
								pricing: BasketNS.Pricing.deserialize(<BasketNS.Pricing.FinalPricing.ISerialized & BasketNS.Pricing.BoundPricing.ISerialized>CIExtension.pricingInfo.pricing),
								volumeComputationMethod: CIExtension.pricingInfo.volumeComputationMethod
							},
							type: 'image',
							configurableMaterial: '',
							materialVariation: '',
							sourceFileURL: imgAsString,
							sourceFileType: '',
							productionFileURL: imgAsString,
							productionFileType: '',
							displayFileURL: imgAsString,
							rotation: CIInfo.rotate,
							position: [CIInfo.leftRight, CIInfo.upDown],
							scaling: [CIInfo.resize, CIInfo.resize],
							crop: [0, 0],
							computedPrice: BasketNS.Price.ofZero()
						});
					}
					CIIdx++;
				}
				return out;
			};
		};
		async instantiate(
			rootLocalId: number,
			appendType: 'drop',
			basketPart: string[] | null,
			basketUseDynamicTitleBinding: boolean,
			metadata: Record<string, unknown> | null
		): Promise<ViewerComponent.ProductInstance>;
		async instantiate(
			rootLocalId: number,
			appendType: 'insert',
			basketPart: string[] | null,
			basketUseDynamicTitleBinding: boolean,
			metadata: Record<string, unknown> | null,
			matrixOverride?: Record<string, ViewerComponent.TMatrix>
		): Promise<ViewerComponent.ProductInstance>;
		async instantiate(
			rootLocalId: number,
			appendType: 'batch',
			basketPart: string[] | null,
			basketUseDynamicTitleBinding: boolean,
			metadata: Record<string, unknown> | null,
			runBatch: (concreteNodes: ReturnType<Product['concretizeProductNode']>) => Promise<void>,
			matrixOverride?: Record<string, ViewerComponent.TMatrix>
		): Promise<ViewerComponent.ProductInstance>;
		async instantiate(
			rootLocalId: number,
			appendType: 'drop' | 'insert' | 'batch',
			basketPart: string[] | null,
			basketUseDynamicTitleBinding: boolean,
			metadata: Record<string, unknown> | null,
			...others: unknown[]
		) {
			const concreteProductNode = this.concretizeProductNode(
				rootLocalId
			);
			const concreteRootPN = (
				concreteProductNode.nodes[0] as Required<typeof concreteProductNode.nodes[0]>
			);
			const currentProduct = app.currentProduct;
			const currentLanguage = app.currentLanguage;
			if (!currentProduct) {
				app.viewer.component_throwUserError('No current product!', {});
			}
			// const newInstancesCount = this.instances.size + 1;
			// const pricesStore = this.extension?.pricing?.[ '*' ]; // TODO : should use resolveSpecificKey to get the key!
			// const furniturePrice = pricesStore ? STD.misc.getDataInFunctionOfQuantity(pricesStore, newInstancesCount) : null;
			const self = this;
			let instance: ViewerComponent.ProductInstance = <any>null;
			let productPart = null;
			// #######################################################################
			// #######################################################################
			// #######################################################################
			// #######################################################################
			// #######################################################################
			if (basketPart) {
				const productOrPart = !basketPart.length ? currentProduct : currentProduct.getPart(basketPart);
				const optionsDescriptors: Omit<BasketNS.Option.IDescriptor, 'part'>[] = [];
				let defaultOption: string;
				if (this.extension && this.extension.presets && this.extension.presets.length) {
					for (const preset of this.extension.presets) {
						optionsDescriptors.push({
							id: preset.id,
							title: <string>preset.name[currentLanguage],
							reference: {
								value : preset.id
							},
							pricingInfo: preset.pricingInfo,
							technicalFees: null,
							metaData: {},
							components: [],
							thumbnail: {
								type: 'image',
								value: preset.iconURL
							}
						});
					}
					if (!this.extension.defaultPreset) {
						throw STD.errors.user('Product with presets without a default one defined!', {
							product: this
						});
					}
					if (typeof this.extension.defaultPreset === 'string') {
						defaultOption = this.extension.defaultPreset;
					} else {
						if (!this.extension.resolveSpecificKey) {
							throw STD.errors.user(
								'Product with dynamic preset default without resolveSpecificKey method in extension!',
								{
									product: this
								}
							);
						}
						const key = await this.extension.resolveSpecificKey.call(this, 'defaultPreset');
						const foundOption = this.extension.defaultPreset[key];
						if (!foundOption) {
							throw STD.errors.user(
								'Product with dynamic preset default has unresolved key!',
								{
									product: this,
									key
								}
							);
						}
						defaultOption = foundOption;
					}
				} else {
					optionsDescriptors.push({
						id: 'std',
						title: '',
						reference: {
								value : 'std'
							},
						pricingInfo: null,
						technicalFees: null,
						metaData: {},
						components: [],
						thumbnail : {
							type : 'image',
							value : this.thumbnailURL
						}
					});
					defaultOption = 'std';
				}
				const partDescriptor: Parameters<typeof productOrPart.createPart>[0] = {
					id: `${this.SKU}-${concreteRootPN.localId}`,
					title: this.name,
					reference: BasketNS.Reference.deserialize({value: this.SKU}),
					metaData: <any>{
						D3Component: true
					},
					parts: [],
					useDynamicTitleBinding: basketUseDynamicTitleBinding,
					options: optionsDescriptors,
					pricing: this.extension?.pricing?.duplicate(),
					defaultOption
				};
				if (basketUseDynamicTitleBinding) {
					Object.defineProperty(partDescriptor, 'title', {
						get(): string {
							return (instance && instance.name) ? instance.name : self.name;
						}
					});
					Object.freeze(partDescriptor);
				}
				productPart = productOrPart.createPart(partDescriptor);
			}
			// #######################################################################
			// #######################################################################
			// #######################################################################
			// #######################################################################
			// #######################################################################
			instance = new ViewerComponent.ProductInstance(
				this,
				concreteRootPN.localId,
				concreteProductNode.mappings,
				productPart,
				basketUseDynamicTitleBinding,
				metadata
			);
			const defaultPreset = await instance.getDefaultPreset();
			if (defaultPreset) {
				concreteRootPN.configuration = {
					presetName: defaultPreset
				};
			}
			if (instance.basketPart) {
				(<any>instance.basketPart.metaData).nodeLocalId = instance.localId;
				await instance.basketPart.initialize();
				if (!instance.basketPart.currentOption) {
					throw STD.errors.library('', {instance});
				}
				const CIExtensions = this.extension?.customImages;
				if (CIExtensions) {
					const CIGetter = this.generateBasketOptionCustomImagesGetter(instance, CIExtensions);
					Object.defineProperty(instance.basketPart.currentOption, 'customImages', {
						get() {
							return CIGetter();
						}
					});
					// FIXTURE for pdf generation!
					Object.defineProperty(instance.basketPart.currentOption.metaData, 'customImages', {
						get() {
							return Array.from(instance.basketPart?.currentOption?.customImages || []);
						},
						enumerable: true
					});
				}
			}
			this.instances.add(instance);
			ViewerComponent.self.safe.value.productInstances.add(instance);
			// #######################################################################
			// #######################################################################
			// #######################################################################
			// #######################################################################
			// #######################################################################
			if (appendType === 'batch') {
				if (!others.length || (typeof others[0] !== 'function') || (typeof others[1] !== 'object')) {
					throw STD.errors.user('', {});
				}
				const runBatch: (concreteNodes: ReturnType<ViewerComponent.Product['concretizeProductNode']>) => Promise<void> = <any>others[0];
				const matrixOverride = <Record<string, ViewerComponent.TMatrix>>others[1];
				if (matrixOverride) {
					for (const [nodeId, newMatrix] of Object.entries(matrixOverride)) {
						const nodeLocalId = concreteProductNode.mappings[nodeId];
						if (!nodeLocalId) {
							throw STD.errors.user('nodeLocalId not found!', {
								nodeId,
								idMappings: concreteProductNode.mappings,
								Product: this,
								instance
							});
						}
						const pn = concreteProductNode.nodes.find(x => x.localId === nodeLocalId);
						if (!pn) {
							throw STD.errors.user('ProductNode not found!', {
								nodeLocalId,
								matrixOverride,
								Product: this,
								instance
							});
						}
						(<STD.object.SetWritable<typeof pn, 'matrix'>>pn).matrix = newMatrix; // We can set matrix writable because we are the owner of the matrix.
					}
				}
				await runBatch(concreteProductNode);
			} else {
				if (appendType === 'drop') {
					await app.viewer.startProductNodeDrop(concreteProductNode.nodes.map(x => ({
						...x,
						// snappable :           false,
						// fixedPositionSetIds : null
					})));
					// await instance.applyDefaultPreset();
				} else if (appendType === 'insert') {
					if (others.length && (typeof others[0] !== 'object')) {
						throw STD.errors.user('', {});
					}
					const matrixOverride = <Record<string, ViewerComponent.TMatrix>>others[0];
					if (matrixOverride) {
						for (const [nodeId, newMatrix] of Object.entries(matrixOverride)) {
							const nodeLocalId = concreteProductNode.mappings[nodeId];
							if (!nodeLocalId) {
								throw STD.errors.user('nodeLocalId not found!', {
									nodeId,
									idMappings: concreteProductNode.mappings
								});
							}
							const pn = concreteProductNode.nodes.find(x => x.localId === nodeLocalId);
							if (!pn) {
								throw STD.errors.user('ProductNode not found!', {
									nodeLocalId,
									matrixOverride,
									Product: this,
									instance
								});
							}
							(<STD.object.SetWritable<typeof pn, 'matrix'>>pn).matrix = newMatrix;
							if (nodeId === 'global') {
								const position = ViewerComponent.positionFromMatrix(newMatrix);
								instance.position.scene = {
									...position.position,
									rotation: {...position.rotation}
								};
							}
						}
					}
					await app.viewer.freezeRendering({keepCount: true});
					await app.viewer.addProductNodesInstances(
						<any>concreteProductNode.nodes
					);
					// await instance.applyDefaultPreset();
					await app.viewer.unfreezeRendering({keepCount: true});
				} else {
					throw new Error;
				}
			}
			// #######################################################################
			// #######################################################################
			// #######################################################################
			// #######################################################################
			// #######################################################################
			await ViewerComponent.self.dispatchEvent('productAdded', {
				details: instance
			});
			return instance;
		};
		/**
		 * Load saved instances from a previous session.
		 **/
		async loadInstance(
			savedProduct: BasketNS.Product.ISerialized,
			serializedInstance: ProductInstance.ISerialized
		): Promise<ProductInstance> {
			const currentLanguage = app.currentLanguage;
			let instance: ViewerComponent.ProductInstance = <any>null;
			let productPart = null;
			if (serializedInstance.basketPart) {
				if (!app.basket) {
					throw new Error;
				}
				const boundGlobalProductId = serializedInstance.basketPart.slice(0, serializedInstance.basketPart.indexOf('.'));
				const currentProduct = app.basket.basket.products.find(x => x.id === boundGlobalProductId);
				if (!currentProduct) {
					app.viewer.component_throwUserError('No current product!', {});
				}
				const savedBasketPartId = serializedInstance.basketPart.slice(serializedInstance.basketPart.indexOf('.') + 1);
				const savedBasketPart = savedProduct.parts.find(x => x.name === savedBasketPartId);
				if (!savedBasketPart) {
					throw new Error;
				}
				const productOrPart = currentProduct;
				const optionsDescriptors: Omit<BasketNS.Option.IDescriptor, 'part'>[] = [];
				let defaultOption: string;
				if (this.extension && this.extension.presets && this.extension.presets.length) {
					for (const preset of this.extension.presets) {
						optionsDescriptors.push({
							id: preset.id,
							title: <string>preset.name[currentLanguage],
							reference: {
								value : preset.id
							},
							pricingInfo: preset.pricingInfo,
							technicalFees: null,
							metaData: {},
							components: [],
							thumbnail: {
								type: 'image',
								value: preset.iconURL
							}
						});
					}
					if (!this.extension.defaultPreset) {
						throw STD.errors.user('Product with presets without a default one defined!', {
							product: this
						});
					}
					if (typeof this.extension.defaultPreset === 'string') {
						defaultOption = this.extension.defaultPreset;
					} else {
						if (!this.extension.resolveSpecificKey) {
							throw STD.errors.user(
								'Product with dynamic preset default without resolveSpecificKey method in extension!',
								{
									product: this
								}
							);
						}
						const key = await this.extension.resolveSpecificKey.call(this, 'defaultPreset');
						const foundOption = this.extension.defaultPreset[key];
						if (!foundOption) {
							throw STD.errors.user(
								'Product with dynamic preset default has unresolved key!',
								{
									product: this
								}
							);
						}
						defaultOption = foundOption;
					}
				} else {
					const CO = savedBasketPart.currentOption;
					if (CO && CO.name !== 'std' && CO.pricingInfo) {
						optionsDescriptors.push(<any>{
							id: CO.name,
							...CO,
							pricingInfo: <any>CO.pricingInfo ? {
								pricing: <any>BasketNS.Pricing.deserialize(<BasketNS.Pricing.FinalPricing.ISerialized & BasketNS.Pricing.BoundPricing.ISerialized>CO.pricingInfo.pricing),
								volumeComputationMethod: <any>CO.pricingInfo.volumeComputationMethod
							} : null
							// title :         '',
							// reference :     null,
							// pricingInfo :   null,
							// technicalFees : null,
							// metaData :      {},
							// components :    []
						});
						defaultOption = CO.name;
					} else {
						optionsDescriptors.push({
							id: 'std',
							title: '',
							reference: { value : 'std' },
							pricingInfo: null,
							technicalFees: null,
							metaData: {},
							components: [],
							thumbnail: CO?.thumbnail || null
						});
						defaultOption = 'std';
					}
				}
				const partDescriptor: Parameters<typeof productOrPart.createPart>[0] = {
					id: `${this.SKU}-${serializedInstance.localId}`,
					title: this.name,
					reference: BasketNS.Reference.deserialize({value: this.SKU}),
					metaData: <any>{
						D3Component: true
					},
					parts: [],
					useDynamicTitleBinding: serializedInstance.basketUseDynamicTitleBinding,
					options: optionsDescriptors,
					pricing: !savedBasketPart.pricing ?
						null :
						BasketNS.Pricing.deserialize(<BasketNS.Pricing.FinalPricing.ISerialized & BasketNS.Pricing.BoundPricing.ISerialized>savedBasketPart.pricing),
					defaultOption
				};
				if (serializedInstance.basketUseDynamicTitleBinding) {
					Object.defineProperty(partDescriptor, 'title', {
						get(): string {
							return (instance && instance.name) ? instance.name : self.name;
						}
					});
					Object.freeze(partDescriptor);
				}
				productPart = productOrPart.createPart(partDescriptor);
			}
			const metadata: any = STD.object.clone(serializedInstance.metadata);
			if (metadata.customImages) {
				metadata.customImages =
					await Promise.all(metadata.customImages.map((x: any) => ViewerGraphicsNS.Component.deserialize(x)));
			}
			//
			//
			//
			//
			//
			//
			//
			//
			//
			//
			//
			//
			//
			instance = new ViewerComponent.ProductInstance(
				this,
				serializedInstance.localId,
				serializedInstance.idMapping,
				productPart,
				serializedInstance.basketUseDynamicTitleBinding,
				metadata
			);
			//
			//
			//
			//
			//
			//
			if (serializedInstance.position && serializedInstance.position.scene) {
				if (serializedInstance.position.scene.x) {
					instance.position.scene.x = serializedInstance.position.scene.x;
				}
				if (serializedInstance.position.scene.y) {
					instance.position.scene.y = serializedInstance.position.scene.y;
				}
				if (serializedInstance.position.scene.z) {
					instance.position.scene.z = serializedInstance.position.scene.z;
				}
				if (serializedInstance.position && serializedInstance.position.scene.rotation) {
					if (serializedInstance.position.scene.rotation.x) {
						instance.position.scene.rotation.x = serializedInstance.position.scene.rotation.x;
					}
					if (serializedInstance.position.scene.rotation.y) {
						instance.position.scene.rotation.y = serializedInstance.position.scene.rotation.y;
					}
					if (serializedInstance.position.scene.rotation.z) {
						instance.position.scene.rotation.z = serializedInstance.position.scene.rotation.z;
					}
				}
			}
			if (serializedInstance.name && (serializedInstance.name !== self.name)) {
				instance.setName(serializedInstance.name);
			}
			if (serializedInstance.translationPrefix) {
				instance.translationPrefix = serializedInstance.translationPrefix;
			}
			if (instance.basketPart) {
				(<any>instance.basketPart.metaData).nodeLocalId = instance.localId;
				await instance.basketPart.initialize();
				if (!instance.basketPart.currentOption) {
					throw STD.errors.library('', {instance});
				}
				const CIExtensions = this.extension?.customImages;
				if (CIExtensions) {
					const CIGetter = this.generateBasketOptionCustomImagesGetter(instance, CIExtensions);
					Object.defineProperty(instance.basketPart.currentOption, 'customImages', {
						get() {
							return CIGetter();
						}
					});
					// FIXTURE for pdf generation!
					Object.defineProperty(instance.basketPart.currentOption.metaData, 'customImages', {
						get() {
							return Array.from(instance.basketPart?.currentOption?.customImages || []);
						},
						enumerable: true
					});
				}
				instance.currentPreset = serializedInstance.currentPreset;
				if (serializedInstance.currentPreset &&
					instance.basketPart.currentOption.id !== serializedInstance.currentPreset
				) {
					await instance.basketPart.useOption(serializedInstance.currentPreset);
				}
			}
			this.instances.add(instance);
			return instance;
		};
		instance_create() {
		};
		async instance_load(
			serialized: {
				readonly basketProduct: BasketNS.Product.ISerialized,
				readonly instance: ProductInstance.ISerialized
			}
		) {
			void serialized;
			await Promise.resolve();
		};
		async instance_duplicate() {
		};
		serialize(): Product.ISerialized {
			return {
				name: this.name,
				SKU: this.SKU,
				nodes: this.nodes.map(x => ({...x})),
				instances: Array.from(this.instances).map(x => x.serialize()),
				category: this.category.name
			};
		};
		setName(_name: string | STD.translationsManager.TranslationMapping) {
			let name: STD.translationsManager.TranslationMapping;
			if (typeof _name === 'string') {
				name = STD.translationsManager.languages.reduce<STD.translationsManager.TranslationMapping>((a, c) => {
					a[c] = _name;
					return (a);
				}, {});
			} else {
				name = _name;
			}
			if (_name !== 'Unnamed') {
				const trId = this.getNameTranslationId();
				STD.translationsManager.addObject(trId, name);
				this.name = STD.translationsManager.get(trId, STD.translationsManager.language);
			} else {
				this.name = <string>name[STD.translationsManager.language];
			}
		};
		constructor(
			desc: Product.IDescriptor,
			category: ViewerComponent.ProductCategory
		) {
			this.name = desc.name || 'Unnamed';
			this.SKU = desc.SKU;
			this.nodes = Array.from(desc.nodes);
			this.instances = new Set;
			this.category = category;
			this.positionsSetId = desc.positionsSetId || null;
			this.thumbnailURL = STD.net.URL_normalize(
				desc.previewPath ||
				'/bundles/EmersyaWebsitePro/images/default/default_3D_preview_image.png'
			);
			this.iconDOM.src = this.thumbnailURL;
			const extension = app.viewer.productsExtensionById[this.getId()];
			let name: string | STD.translationsManager.TranslationMapping = this.name;
			if (extension) {
				this.extension = extension;
				if (extension.name) {
					name = extension.name;
				}
			}
			this.setName(name);
			/** Cannot await event dispatch because we are in the constructor. **/
			void ViewerComponent.self.dispatchEvent('productTypeAdded', {
				details: this
			});
		};
	};
	export namespace Product {
		/*
			In product, product node id are string to be used as a symbolic reference.
			"root" designate the root local id given during the call of "instantiate".
			Other references are resolved relatives to other nodes in the product
		*/
		export interface INode {
			SKU: string;
			projectCode: string;
			localId: string;
			parentLocalId: string;
			snappable?: boolean;
			fixedPositionSetIds?: string[] | null;
			matrix: ViewerComponent.TMatrix;
			tracked?: boolean;
			draggable?: boolean;
			doNotListAsProductComponent?: boolean;
		};
		export interface IDescriptor {
			name?: string;
			SKU: string; //Not viewer SKU but ecommerce SKU
			previewPath?: string;
			nodes: INode[];
			positionsSetId?: string | null;
		};
		export interface ISerialized {
			readonly name: string;
			readonly SKU: string;
			readonly nodes: INode[];
			readonly instances: ProductInstance.ISerialized[];
			readonly category: string;
		};
	}

		export class ProductInstance {
		product: Product;
		localId: number;
		localsIds: number[];
		idMapping: Record<string, number>;
		readonly metadata: {
			[key: string]: unknown;
			customImages?: TViewerGraphicsValue[];
		};
		basketUseDynamicTitleBinding: boolean;
		currentPreset: string | null = null;
		name: string | null = null;
		basketPart: BasketNS.Part | null;
		currentLocalId: number | null = null;
		boundingBox = null;
		translationPrefix: string | null = null;
		isInInitialDrag: boolean = false;
		isDeleted: boolean = false;
		isLocked: boolean = false;
		position = {
			UI: {
				x: 0,
				y: 0,
				z: 0
			},
			scene: {
				x: 0,
				y: 0,
				z: 0,
				rotation: {
					x: 0,
					y: 0,
					z: 0
				}
			}
		};
		/** Override the one in Product, if any. **/
		// positionsSetId : string | null = null; // Not currently possible to implement.
		get order() {
			return Array.from(this.product.instances).indexOf(this) + 1;
		};
		async duplicate() {
			let matrixOverride: Record<string, ViewerComponent.TMatrix> | undefined;
			try {
				const cfg = await app.viewer.getCurrentProductNodesConfiguration();
				const matricesByLocalId = new Map<number, ViewerComponent.TMatrix>();
				for (const pn of cfg.productNodes) {
					if (this.localsIds.includes(pn.localId)) {
						matricesByLocalId.set(pn.localId, pn.matrix);
					}
				}
				const override: Record<string, ViewerComponent.TMatrix> = {};
				for (const [nodeId, localId] of Object.entries(this.idMapping)) {
					if (nodeId === 'root') {
						continue;
					}
					const mtx = matricesByLocalId.get(localId);
					if (mtx) {
						override[nodeId] = <ViewerComponent.TMatrix>Array.from(mtx);
					}
				}
				if (Object.keys(override).length) {
					matrixOverride = override;
				}
			} catch (_e) {
				matrixOverride = undefined;
			}
			const duplicata = await this.product.instantiate(
				this.idMapping.root!,
				'insert',
				[],
				this.basketUseDynamicTitleBinding,
				this.metadata,
				matrixOverride
			);
			return duplicata;
		};
		getId() {
			return (this.localId);
		};
		getNameTranslationId() {
			if (!this.name) {
				return this.product.getNameTranslationId();
			}
			const prefix = this.translationPrefix || STD.sessionId;
			if (!this.translationPrefix) {
				this.translationPrefix = prefix;
			}
			const translationId = prefix + '.productInstance.' + this.product.getId() + '.' + this.getId() + '.name';
			return translationId;
		};
		setName(name: string) {
			this.name = name;
			const tr = (
				(name[0] === '\0') ? {...STD.translationsManager.store.get(name.slice(1))} :
					STD.translationsManager.languages.reduce<STD.translationsManager.TranslationMapping>((a, c) => {
						a[c] = name;
						return a;
					}, {})
			);
			const trId = this.getNameTranslationId();
			STD.translationsManager.addObject(trId, tr);
			this.name = name;
		};
		private isOnSelection = false;
		serialize(): ProductInstance.ISerialized {
			const metadata = <ProductInstance.ISerialized['metadata']>{...this.metadata};
			if (this.metadata.customImages) {
				metadata.customImages =
					this.metadata.customImages.map<NonNullable<ProductInstance.ISerialized['metadata']['customImages']>[0]>(x => ({
						...x,
						image: (<any>x).image ?
							((typeof x.image === 'string') ? <string><any>(<any>x).image : (<any>x).image.src) :
							null
						// image : x.image ? ((typeof x.image === 'string') ? x.image : x.image.src) : null
					}));
			}
			return {
				product: this.product.SKU,
				localId: this.localId,
				localsIds: this.localsIds,
				idMapping: this.idMapping,
				metadata: metadata,
				currentPreset: this.currentPreset,
				name: this.name,
				basketPart: this.basketPart ? this.basketPart.getPath() : null,
				currentLocalId: this.currentLocalId,
				boundingBox: this.boundingBox,
				translationPrefix: this.translationPrefix,
				position: this.position,
				basketUseDynamicTitleBinding: this.basketUseDynamicTitleBinding
			};
		};
		async select(
			mouseButton: 'left' | 'wheel' | 'right' | null = null,
			meshesNames: string[] = [],
			localId?: number,
			updateDraggingGroup: boolean = true
		) {
			if (ViewerComponent.self.currentSelectedInstances.has(this))
				return;
			ViewerComponent.self.currentSelectedInstances.add(this);
			this.isOnSelection = true;
			this.currentLocalId = localId || this.localId;
			if (updateDraggingGroup)
				await ViewerComponent.self.updateDraggingGroup();
			await ViewerComponent.self.selectProductNode({
				localId: this.localId
			});
			await ViewerComponent.self.dispatchEvent('selection', {
				subType: 'enabled',
				details: <ISelectionEventDetails>{
					productInstance: this,
					isEnabled: true,
					mouseButton,
					meshesNames
				}
			});
			this.isOnSelection = false;
		};
		async unselect(updateDraggingGroup = true) {
			if (this.isOnSelection)
				return; // double-click fix
			if (!ViewerComponent.self.currentSelectedInstances.has(this))
				throw STD.errors.user('Only a selected instance can be unselect!', {
					instance: this
				});
			ViewerComponent.self.currentSelectedInstances.delete(this);
			if (updateDraggingGroup)
				await ViewerComponent.self.updateDraggingGroup();
			this.currentLocalId = null;
			await ViewerComponent.self.unselectProductNode({
				localId: this.localId
			});
			await ViewerComponent.self.dispatchEvent('selection', {
				subType: 'disabled',
				details: <ISelectionEventDetails>{
					productInstance: this,
					isEnabled: false
				}
			});
		};
		async delete() {
			if (this.isDeleted) return;
			this.isDeleted = true;
			ViewerComponent.self.safe.value.productInstances.delete(this);
			this.product.instances.delete(this);
			if (this.basketPart) {
				await this.basketPart.remove();
			}
			if (ViewerComponent.self.currentSelectedInstances.has(this)) {
				await this.unselect();
			}
			await ViewerComponent.self.removeProductNodeInstance(this.localId);
			await ViewerComponent.self.dispatchEvent('productRemoved', {
				details: this
			});
		};
		async setLocked(isLocked: boolean) {
			if (this.isLocked === isLocked)
				return;
			this.isLocked = isLocked;
			await ViewerComponent.self.setProductNodesInstancesDraggable({
				nodes: [{
					localId: this.localId,
					draggable: !isLocked
				}]
			});
		}
		async deleteBatch(
			runBatch: () => Promise<void>
		) {
			ViewerComponent.self.safe.value.productInstances.delete(this);
			this.product.instances.delete(this);
			if (ViewerComponent.self.currentSelectedInstances.has(this)) {
				await this.unselect();
			}
			/** Await other actions of the batch before triggering the event. **/
			await runBatch();
			await ViewerComponent.self.dispatchEvent('productRemoved', {
				details: this
			});
		};
		async updatePositionRaw(
			position: ProductInstance['position']['scene']
		) {
			this.position.scene = position;
			const q = quat.create();
			quat.fromEuler(q, position.rotation.x, position.rotation.y, position.rotation.z);
			const axis = <ViewerComponent.TVector3>vec3.create();
			let angle = quat.getAxisAngle(axis, q);
			if (isNaN(axis[0])) {
				vec3.set(axis, 0, 0, 1);
				angle = 0;
			}
			await app.viewer.setProductNodesInstancesTransformations({
				nodes: [
					{
						localId: this.localId,
						position: [position.x, position.y, position.z],
						rotationAxis: axis,
						rotationAngle: angle
					}
				]
			});
		};
		updatePosition(
			position: { x?: number, y?: number, z?: number, rotation?: { x?: number, y?: number, z?: number } }
		) {
			const fromPosition = structuredClone(this.position.scene);
			let modified = false;
			for (const key of ['x', 'y', 'z'] as const) {
				if (position[key] !== undefined && position[key] !== this.position.scene[key]) {
					modified = true;
					this.position.scene[key] = position[key];
				}
			}
			if (position.rotation !== undefined) {
				for (const key of ['x', 'y', 'z'] as const) {
					if (position.rotation[key] !== undefined && position.rotation[key] !== this.position.scene.rotation[key]) {
						modified = true;
						this.position.scene.rotation[key] = position.rotation[key];
					}
				}
			}
			if (modified) {
				this.updatePositionDebounced(this.localId, fromPosition, this.position.scene);
			}
		};
		updatePositionDebounced = STD.misc.createDebouncer({
			delay: 50,
			fn: (localId, fromPosition, toPosition) => {
				this.updatePositionRaw(toPosition).then(() => {
					app.historyManager.addExecuted<TPositionCommand>('viewer/position', {
						localId: localId,
						from: fromPosition,
						to: structuredClone(toPosition)
					});
				}).catch(error => app.component_throwLibraryError('Failed to update position', {error}));
			}
		});
		async usePreset(presetId: string) {
			const e = this.product.extension;
			if (!e || !e.presets) {
				throw STD.errors.user('Product does not have presets!', {
					productInstance: this,
					presetId
				});
			}
			const preset = e.presets.find(x => x.id === presetId);
			if (!preset) {
				throw STD.errors.user(`Preset<${preset}> not found!`, {
					productInstance: this,
					preset
				});
			}
			await app.viewer.applyOption({
				nodeLocalIds: [this.localId],
				...preset.option
			});
			this.currentPreset = presetId;
			if (!this.product.extension) {
				throw STD.errors.library('', {}); // impossible because presets are registered in extension...
			}
			if (this.basketPart) {
				await this.basketPart.useOption(presetId);
			}
			if (this.product.extension.onPresetChanged) {
				await this.product.extension.onPresetChanged.call(this);
			}
		};
		async applyDefaultPreset() {
			if (!this.product.extension || !this.product.extension.presets || this.product.extension.presets.length === 0) {
				return;
			}
			if (this.currentPreset) {
				throw STD.errors.library('First instantiation, this shouldn\'t have a current preset!', {productInstance: this});
			}
			const defaultPreset = this.product.extension.defaultPreset;
			if (!defaultPreset) {
				throw STD.errors.library('Missing default preset!', {productInstance: this});
			}
			if (!app.currentProduct) {
				throw STD.errors.library('No app current product!', {productInstance: this});
			}
			if (typeof defaultPreset === 'string') {
				await this.usePreset(defaultPreset);
			} else {
				const defaultPresetId = defaultPreset[app.virtualCurrentProductSKU];
				if (!defaultPresetId) {
					throw STD.errors.user('Missing default preset for this product!', {productInstance: this});
				}
				await this.usePreset(defaultPresetId);
			}
		};
		async getDefaultPreset() {
			if (!this.product.extension || !this.product.extension.presets || this.product.extension.presets.length === 0) {
				return;
			}
			if (this.currentPreset) {
				throw STD.errors.library('First instantiation, this shouldn\'t have a current preset!', {productInstance: this});
			}
			const defaultPreset = this.product.extension.defaultPreset;
			if (!defaultPreset) {
				throw STD.errors.user('Missing default preset!', {productInstance: this});
			}
			if (!app.currentProduct) {
				throw STD.errors.library('No app current product!', {productInstance: this});
			}
			if (typeof defaultPreset === 'string') {
				this.currentPreset = defaultPreset;
				if (this.basketPart) {
					await this.basketPart.useOption(defaultPreset);
				}
				return defaultPreset;
			} else {
				const defaultPresetId = defaultPreset[app.virtualCurrentProductSKU];
				if (!defaultPresetId) {
					throw STD.errors.user('Missing default preset for this product!', {productInstance: this});
				}
				this.currentPreset = defaultPresetId;
				if (this.basketPart) {
					await this.basketPart.useOption(defaultPresetId);
				}
				const preset = this.product.extension.presets.find(x => x.id === defaultPresetId);
				if (!preset) {
					throw STD.errors.user('No preset associated with this id!', {
						productInstance: this,
						defaultPresetId
					});
				}
				const name = (<IOptionPresetByName>preset.option).name;
				const SKU = (<IOptionPresetBySKU>preset.option).SKU;
				if (!SKU && !name) {
					throw STD.errors.library('Need a preset name or SKU!', {
						productInstance: this,
						preset
					});
				}
				return name ? name : SKU;
			}
		};
		resolvePresetToBasketOption(preset: ViewerComponent.IProductPreset) {
			if (!this.basketPart) {
				throw STD.errors.user(`No basket part!`, {
					productInstance: this,
				});
			}
			const option = this.basketPart.options.find(x => x.id === preset.id);
			if (!option) {
				throw STD.errors.user(`No option exists for this preset<${preset.id}>!`, {
					productInstance: this,
					preset
				});
			}
			return option;
		};
		constructor(
			product: Product,
			localId: number,
			idMapping: Record<string, number>,
			basketPart: BasketNS.Part | null,
			basketUseDynamicTitleBinding: boolean,
			metadata: Record<string, unknown> | null
		) {
			this.product = product;
			this.localId = localId;
			this.metadata = metadata || {};
			this.boundingBox = null;
			this.idMapping = idMapping;
			this.localsIds = Object.values(STD.object.copyWithout(this.idMapping, ['root']));
			this.basketPart = basketPart;
			this.basketUseDynamicTitleBinding = basketUseDynamicTitleBinding;
		};
	};
	export namespace ProductInstance {
		export interface ISerialized {
			readonly product: string; // SKU
			readonly localId: number;
			readonly localsIds: number[];
			readonly idMapping: Record<string, number>;
			readonly metadata: {
				[key: string]: unknown;
				customImages?: IViewerGraphicsValueSerialized[];
			};
			readonly currentPreset: string | null;
			readonly name: string | null;
			readonly basketPart: string | null; // part path
			readonly currentLocalId: number | null;
			readonly translationPrefix: string | null;
			readonly boundingBox: null;
			readonly basketUseDynamicTitleBinding: boolean;
			readonly position: {
				readonly UI: {
					readonly x: number;
					readonly y: number;
					readonly z: number;
				};
				readonly scene: {
					readonly x: number;
					readonly y: number;
					readonly z: number;
					readonly rotation: {
						readonly x: number;
						readonly y: number;
						readonly z: number;
					};
				};
			};
		};
	}

	interface Task<TContext = unknown, TReturn = unknown> {
	context: TContext;
	func: (context: TContext) => Promise<TReturn>;
	resolve: (value: TReturn) => void;
	reject: (value?: any) => void;
}
export class TaskQueue {
	currentTask?: Task;
	queue: Task[] = [];
	push<
		TContext,
		TFunc extends (context: TContext) => Promise<unknown>,
		TReturn extends Awaited<ReturnType<TFunc>>
	>(context: TContext, func: TFunc): Promise<TReturn> {
		const { promise, resolve, reject } = STD.misc.createPromisePack<TReturn>();
		this.queue.push(<Task>{
			context,
			func,
			resolve,
			reject
		});
		<void><unknown>this.run();
		return promise;
	}
	private async run() {
		if (this.currentTask !== undefined)
			return;
		while (this.queue.length > 0) {
			this.currentTask = this.queue.shift()!;
			try {
				const value = await this.currentTask.func(this.currentTask.context);
				this.currentTask.resolve(value);
			} catch (error) {
				this.currentTask.reject(error);
			}
			this.currentTask = undefined;
		}
	}
}

	

}
export namespace NS {
	export const Component = ViewerComponent;
	export type Component = ViewerComponent;
	export type Configuration = IViewerComponentConfiguration;
	export type Value = IViewerData;
	export type ValueSerialized = IViewerSerialized;
}
export default ViewerComponent;
import './Viewer.scss';
CM.Manager.declare({
	id: 'viewer',
	type: ViewerComponent
});
 import { TViewerGraphicsValue, IViewerGraphicsValueSerialized, NS as ViewerGraphicsNS }          from '../Graphics/ViewerGraphics';
 import type { EngineApplicationComponent as TApplicationComponent } from '../../Application/Application';
 import { PlannerComponent }              from '../planner/Planner/ViewerPlanner';
 import { NS as ViewerMenuNS }            from '../Menu/ViewerMenu';
 import { NS as HistoryManagerNS } from '../../HistoryManager';
